import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { fetchAdminClaim, isAdminUser } from './utils/adminClaim';
import { loadBillingDecision, isFreeNow, isLiveSub, loadTariff, decisionHasTariff, resolveBillingOwner, computeAccessMode, ACCESS_MODES } from './utils/billingPolicy';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import LabDirectory from './components/LabDirectory';
import PublicLabDirectory from './components/PublicLabDirectory';
import LabProfile from './components/LabProfile';
// 🦷 테스트보철(A/S 무료제작권) 중개
import TestCaseBoard from './components/TestCaseBoard';
import CreateTestRequest from './components/CreateTestRequest';
import TestCaseDetail from './components/TestCaseDetail';
// 🦷 A/S 재제작 상담·연결 (치과 주도·명단선택·관리자 중개) — 기존 /test-cases 공개게시판 대체 진입점
import AsRemakeRequest from './components/AsRemakeRequest';


// 레이아웃
import MainLayout from './components/MainLayout';

// 인증 페이지
import Login from './components/Login';
import AgentEmbedEntry from './components/AgentEmbedEntry';
import Register from './components/Register';
import PrivacyPolicy from './components/PrivacyPolicy';
import LabPromoStats from './components/LabPromoStats';

// 메인 기능 페이지
import Dashboard from './components/Dashboard';
import CreateOrder from './components/CreateOrder';
import OrderList from './components/OrderList';
import ViewOrder from './components/ViewOrder';
import ViewMessage from './components/ViewMessage';
import ConnectionList from './components/ConnectionList';
import Profile from './components/Profile';
import Statistics from './components/Statistics';
import TransactionStatementList from './components/TransactionStatementList';
import SensitiveGate from './components/SensitiveGate';
import ClinicSubGate from './components/ClinicSubGate';   // 치과 구독 잠금 (2026-08-21, 스위치 OFF 선배포)
import PartnerOverview from './components/PartnerOverview';
import SecurityLockSettings from './components/SecurityLockSettings';
import Marketplace from './components/Marketplace';
import AdManager from './components/AdManager';
import TermsOfService from './components/TermsOfService';
import NaverReviewManager from './components/NaverReviewManager';
import ProfileAdManager from './components/ProfileAdManager';
import CalendarView from './components/CalendarView';
import SellerApplication from './components/SellerApplication';
import SellerApplicationStatus from './components/SellerApplicationStatus';
import ProductRegistration from './components/ProductRegistration';
import ProductManagement from './components/ProductManagement';
import BulkProductUpload from './components/BulkProductUpload';
import SellerDashboard from './components/SellerDashboard';
import SellerOrderList from './components/SellerOrderList';
import SellerSettlement from './components/SellerSettlement';
import SellerStoreSettings from './components/SellerStoreSettings';
import SellerStorefront from './components/SellerStorefront';
import AdminSellerApproval from './components/AdminSellerApproval';
import AdminProductApproval from './components/AdminProductApproval';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import OrderComplete from './components/OrderComplete';
import AdminAdApproval from './components/AdminAdApproval';
import AdRegistration from './components/AdRegistration';

// 🆕 통합 결제 시스템 관리자 페이지
import AdminLabApproval from './components/admin/AdminLabApproval';
import AdminGlobalLabs from './components/admin/AdminGlobalLabs';
import JoinGlobal from './components/global/JoinGlobal';
import GlobalStatus from './components/global/GlobalStatus';
import GlobalFindLabs from './components/global/GlobalFindLabs';
import GlobalLanding from './components/global/GlobalLanding';
import GlobalOrders from './components/global/GlobalOrders';
import GlobalInvoices from './components/global/GlobalInvoices';
import GlobalAgentDownload from './components/global/GlobalAgentDownload';
import GlobalLabOrders from './components/global/GlobalLabOrders';
import AdminGlobalCommission from './components/admin/AdminGlobalCommission';
import PaymentSettings from './components/settings/PaymentSettings';
import AgentDevices from './components/settings/AgentDevices';
import ClinicProgressShare from './components/settings/ClinicProgressShare';
import ClinicIntakeFields from './components/settings/ClinicIntakeFields';   // 🩺 첫 방문 등록 항목 설정 (2026-09-30, 플래그 patientIntake)
import ClinicFeedbackSettings from './components/settings/ClinicFeedbackSettings';   // 🩺 장착 후 확인 설정 (2026-10-02, 플래그 patientFeedback strict — 페이지 안에서 게이트)
import ClinicPatientShareSettings from './components/settings/ClinicPatientShareSettings';   // 🦷 환자 공개 범위 (2026-10-14, 플래그 clinicPatientShareUi strict — 페이지 안에서 게이트)
import ClinicRoadmapSettings from './components/settings/ClinicRoadmapSettings';   // 🗺️ 진료 후 질문 설정 (2026-10-06, 플래그 roadmapEngine strict — 페이지 안에서 게이트)
import ClinicWalkinQr from './components/settings/ClinicWalkinQr';   // 🪪 웹 접수 QR 설정 (2026-10-01, 플래그 clinicWalkin)
import ClinicDelegationSettings from './components/settings/ClinicDelegationSettings';   // 🛡️ 설정 › 환자 정보 처리위탁 (2026-10-08 — 부속 확인 2건 상태·확인·환자 앱 철회 한 곳)
import AdminLabAdvertising from './components/admin/AdminLabAdvertising';
import AdminAnalytics from './components/admin/AdminAnalytics';
// AdminJobPostingApproval removed - 결제 승인 불필요 (무료 등록)
import AdminMarketplaceApproval from './components/admin/AdminMarketplaceApproval';
import UsedItemsList from './components/UsedItemsList';
import UsedItemRegistration from './components/UsedItemRegistration';
import UsedItemDetail from './components/UsedItemDetail';
import ProductEdit from './components/ProductEdit';
import OrderManagement from './components/OrderManagement';
import ProductDetail from './components/ProductDetail';
import SeminarApproval from './components/SeminarApproval';


// 개인/업체 회원 기능
import StaffJoin from './components/StaffJoin';
import ReceivedInvites from './components/ReceivedInvites';
import CompanyManager from './components/CompanyManager';
import StaffAffiliation from './components/StaffAffiliation';
import CompanyRegister from './components/CompanyRegister';
import StaffInviteManagement from './components/StaffInviteManagement';
import SalesRepManagement from './components/SalesRepManagement';

// 🆕 커뮤니티 기능
import JobBoard from './components/JobBoard';
import NewProducts from './components/NewProducts';
import Seminars from './components/Seminars';
import GovSupport from './components/GovSupport';
import CommunityBoard from './components/CommunityBoard';
import Exhibition from './components/Exhibition';
import Clinical from './components/Clinical';
import DentalNews from './components/DentalNews';
import CEEducation from './components/CEEducation';
import Interior from './components/Interior';
import Cleaning from './components/Cleaning';
import CommunityHub from './components/CommunityHub';
import LabFeed from './components/LabFeed';
import LabPortfolioGallery from './components/LabPortfolioGallery';
import EmpathyBoard from './components/EmpathyBoard';

// 🆕 관리자 커뮤니티 관리 기능
import AdList from './components/AdList';
import JobPostManagement from './components/JobPostManagement';
import SeminarManagement from './components/SeminarManagement';
import MarketplaceProductManagement from './components/MarketplaceProductManagement';

// 🆕 DC마켓 리뉴얼 (v4)
import AdminMarketDashboard from './components/AdminMarketDashboard';
import AdminSettlementManagement from './components/AdminSettlementManagement';

// 🆕 기공소 설정
import LabEmailSettings from './components/lab/LabEmailSettings';

// 🆕 구독 / 결제
import AppSubscription from './components/AppSubscription';
import BillingOverview from './components/BillingOverview';

// 🆕 관리자 구독
import AdminSubscriptionDashboard from './components/admin/AdminSubscriptionDashboard';

// 🆕 피드백 및 구매내역
import FeedbackForm from './components/FeedbackForm';
import MyOrders from './components/MyOrders';
import MarketplaceLayout from './components/MarketplaceLayout';

// 🆕 마켓플레이스 Phase 3
import RefundRequest from './components/RefundRequest';
import RefundManagement from './components/RefundManagement';

// 🆕 관리자 추가 기능
import AdminFeedbackList from './components/AdminFeedbackList';
import AdminPaymentSettings from './components/AdminPaymentSettings';
import AdminStoreManagement from './components/AdminStoreManagement';
import AdminUserManagement from './components/AdminUserManagement';
import AdminUsedItemManagement from './components/AdminUsedItemManagement';
import AdminPageToggle from './components/AdminPageToggle';
import AdminMatchingManagement from './components/AdminMatchingManagement';
import AdminCommissions from './components/admin/AdminCommissions';
import AdminTestCases from './components/admin/AdminTestCases';
import EmergencyOutsource from './components/EmergencyOutsource';
// import DesignOutsource from './components/DesignOutsource';   // 연결형 P0(10-15): /lab-outsource 리다이렉트로 미사용 — 화면은 OutsourceRequestPage › CadDesignHome 이 가져온다
import OutsourceRequestPage from './components/OutsourceRequestPage';
import VendorStatements from './components/outsourceMf/VendorStatements';   // 외주업체 전용 거래명세서(10-30, 기존 거래명세서와 코드 분리)
import VendorRegister from './components/outsourceMf/VendorRegister';
import { getVendorProfile } from './services/outsourceMfApi';   // S5(10-27): 외주 프로필 보유 계정은 outsourceMf 플래그 무관하게 설정 열림
import AdminVendorReview from './components/admin/AdminVendorReview';
import StatementShareView from './components/StatementShareView';
import RevenueQuickView from './components/RevenueQuickView';
import MissingClaims from './components/MissingClaims';
import AdminGallerySponsors from './components/admin/AdminGallerySponsors';
import ClinicSettlement from './components/ClinicSettlement';
import AsLanding from './components/AsLanding';
import LocationTerms from './components/LocationTerms';
import LabLanding from './components/LabLanding';
import AdminAsLeads from './components/admin/AdminAsLeads';
import AdminNewClinics from './components/admin/AdminNewClinics';
import AdminClinicDirectory from './components/admin/AdminClinicDirectory';
import AdminNewLabs from './components/admin/AdminNewLabs';
import CommissionList from './components/CommissionList';
import AdminMatchRequests from './components/admin/AdminMatchRequests';
import AdminAsRemakeRequests from './components/admin/AdminAsRemakeRequests';
// import AdminCommission from './components/admin/AdminCommission'; // 수수료 제거
import AdminReferrals from './components/admin/AdminReferrals';
import AdminHiraSync from './components/admin/AdminHiraSync';
import AdminClaimMigration from './components/admin/AdminClaimMigration';
import AdminHospitalClaimReview from './components/admin/AdminHospitalClaimReview';
import AdminClinicClaimManager from './components/admin/AdminClinicClaimManager';
import AdminHiraNonpaymentSync from './components/admin/AdminHiraNonpaymentSync';
import AdminPublicDataSync from './components/admin/AdminPublicDataSync';
import AdminCareRecommendations from './components/admin/AdminCareRecommendations';
// import ClinicPatientReservations from './components/clinic/ClinicPatientReservations';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// import ClinicSendMedicalRecord from './components/clinic/ClinicSendMedicalRecord';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
import ClinicRecentScans from './components/clinic/ClinicRecentScans';
import AgentDownload from './components/AgentDownload';
import JigLocal from './components/JigLocal';
import JigWeb from './components/JigWeb';
import Jig from './components/Jig';
import DesignResults from './components/DesignResults';
// 🏭 CNC 파트너(제조사) 화면 — UI 구조 단계(샘플 데이터, 서버 연결 전) (2026-10-23)
import PartnerHome from './components/partner/PartnerHome';
import PartnerCases from './components/partner/PartnerCases';
import PartnerLabs from './components/partner/PartnerLabs';
import PartnerSettlement from './components/partner/PartnerSettlement';
import PartnerSettings from './components/partner/PartnerSettings';
import HandwrittenOrder from './components/HandwrittenOrder';
import LabHandwrittenTemplate from './components/lab/LabHandwrittenTemplate';
import ClinicWelcome from './components/clinic/ClinicWelcome';
import ClinicProfile from './components/clinic/ClinicProfile';
// import ClinicPatientAppPromo from './components/clinic/ClinicPatientAppPromo';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// import ClinicMyPatients from './components/clinic/ClinicMyPatients';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// 치과 환자관리 1단계 (2026-09-10) — 플래그 clinicPatients 뒤, 환자 웹 /p/ 트리 별도
import ClinicPatients from './components/clinic/ClinicPatients';
import DeskReception from './components/clinic/DeskReception';   // 🧾 치과 「접수」 데스크 (2026-10-03, 플래그 deskReception strict)
import DoctorCallScreen from './components/clinic/doctorCall/DoctorCallScreen';   // 🔔 원장 호출 전체 화면 /call (2026-10-08, 플래그 doctorCall strict)
import DoctorCallSettings from './components/settings/DoctorCallSettings';   // 🔔 설정 › 원장 호출 (2026-10-08, 플래그 doctorCall strict — 페이지 안에서 게이트)
import ClinicRecalls from './components/clinic/ClinicRecalls';   // 🔔 재내원 안내 (2026-10-01, 플래그 clinicRecall strict)
import ClinicAlerts from './components/clinic/ClinicAlerts';   // 🔔 환자 자동관리 › 알림 (2026-10-08, 플래그 clinicRecall || clinicNotify strict)
import ClinicBookings from './components/clinic/ClinicBookings';   // 📅 환자 자동관리 › 예약 (2026-10-11, 플래그 careBooking strict + 서버 available — 파일럿 치과만)
import ClinicPatientDetail from './components/clinic/ClinicPatientDetail';
// 환자 웹 등록 폐지 → 앱 전용(2026-09-10 저녁 대표 확정): /p/c/:token·/p/app = 앱 설치 안내만, /p/me·인증 코드 삭제
import PatientQrLanding from './components/patient-web/PatientQrLanding';
import ClinicWalkin from './components/patient-web/ClinicWalkin';   // 🪪 /walkin/:slug 웹 접수 공개 페이지 (2026-10-01)
import PatientShell from './components/patient-web/PatientShell';
// import ClinicOffersManager from './components/clinic/ClinicOffersManager';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// import ClinicProfileCompleteness from './components/clinic/ClinicProfileCompleteness';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// import ClinicPatientNotify from './components/clinic/ClinicPatientNotify';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
// import ClinicAnalytics from './components/clinic/ClinicAnalytics';   // 구 환자앱 폐기 09-14, 폐기 계획 승인 후 제거
import PublicClinicDirectory from './components/PublicClinicDirectory';
import AdminLocaldataSync from './components/admin/AdminLocaldataSync';

// 환자용 (모바일 앱 우선, 웹은 다운로드 유도)
import PatientAppLanding from './components/PatientAppLanding';
import AccountDeleteGuide from './components/AccountDeleteGuide';
// 🦷 덴클 케어(분리 환자앱) 공개 페이지 — 약관 p-1.0·계정 삭제 안내 (2026-10-04 W7·W8, 문안 미확정이면 '준비 중')
import CareTerms from './components/care/CareTerms';
import CareAccountDelete from './components/care/CareAccountDelete';
import PatientTerms from './components/PatientTerms';
import PatientPrivacy from './components/PatientPrivacy';
// import CommissionNotice from './components/CommissionNotice'; // 수수료 제거

// 🆕 매칭 서비스
import MatchingSubscription from './components/MatchingSubscription';

// 재료상 추천인
import SellerReferralLink from './components/SellerReferralLink';

// 🆕 기공소 프로모션 랜딩페이지
import IvanPromoLanding from './components/IvanPromoLanding';
import LabPromoLanding from './components/LabPromoLanding';
import LabRecruitLanding from './components/LabRecruitLanding';
import DesignMarketPromo from './components/DesignMarketPromo';
import BannerLab from './components/BannerLab';
import LabPromoSettings from './components/lab/LabPromoSettings';
import LabVisitorStats from './components/lab/LabVisitorStats';
import ChatRoom from './components/ChatRoom';

import LabAdvertising from './components/LabAdvertising';
import OutsourceAdvertising from './components/OutsourceAdvertising';

// 🆕 세금계산서 시스템
import TaxInvoiceList from './components/TaxInvoiceList';
import CreateTaxInvoice from './components/CreateTaxInvoice';
import TaxInvoiceDetail from './components/TaxInvoiceDetail';

// 🆕 메뉴 통합 Hub 컴포넌트
import ScheduleHub from './components/ScheduleHub';
import StatementsHub from './components/StatementsHub';
import StatisticsHub from './components/StatisticsHub';
import SettingsHub from './components/SettingsHub';
import ClinicInvites from './components/ClinicInvites';
import ProductionBoard from './components/ProductionBoard';
import CaseCheckPreview from './components/CaseCheckPreview';
import CaseCheckPage from './components/CaseCheckPage';
import BusinessAnalysis from './components/BusinessAnalysis';
import RemakeAnalysis from './components/RemakeAnalysis';
import LabCostAnalysis from './components/LabCostAnalysis';
import ClinicToday from './components/ClinicToday';
import PublicHome from './components/PublicHome';
import PublicRequests from './components/PublicRequests';
import PublicGallery from './components/PublicGallery';
import { getFeatures, isBetaUser, isClinicScanIntakeOn, isClinicPatientsOn, isClinicRecallOn, isClinicAlertsOn, isDeskReceptionOn, isDoctorCallOn, isCareBookingOn, stageOpen, isOutsourceMfOn, isJigLocalOn, isJigWebOn, isDesignResultsOn, isPartnerHomeOn } from './utils/features';
import { useDelegationV70 } from './utils/delegationV70';   // 📜 약관 v7.0 제4조의7 14항 — 기존 치과 회원 재확인 전 환자관리 잠금(시행 2026-11-06 후)
import DelegationV70Gate from './components/clinic/DelegationV70Gate';


// 공개 페이지 Login/Register 래퍼 (navigate 사용)
function PublicLoginWrapper({ onLogin }) {
  const navigate = useNavigate();
  return <Login onLogin={onLogin} onShowRegister={() => navigate('/signup')} />;
}
function PublicRegisterWrapper({ onRegister }) {
  const navigate = useNavigate();
  return <Register onRegister={onRegister} onShowLogin={() => navigate('/login')} />;
}

// ✨ ViewOrder Wrapper 컴포넌트 (App 컴포넌트 밖에 정의)
function ViewOrderWrapper({ user }) {
  const { orderId } = useParams();
  const navigate = useNavigate();

  return (
    <ViewOrder
      orderId={orderId}
      user={user}
      onBack={() => navigate('/orders')}
    />
  );
}

// 세금계산서 Wrapper 컴포넌트
function TaxInvoiceDetailWrapper({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  // 결제 전 기공소(제한 모드) = 읽기 전용 (계획_가입승인결제흐름 W6, 법무 R4) — 보기·인쇄만
  return <TaxInvoiceDetail invoiceId={id} user={user} onBack={() => navigate('/tax-invoices')} readOnly={user?.accessMode === 'lab_limited'} />;
}

function TaxInvoiceListWrapper({ user }) {
  const navigate = useNavigate();
  return (
    <TaxInvoiceList
      user={user}
      onSelectInvoice={(id) => navigate(`/tax-invoices/${id}`)}
      onCreateNew={user?.accessMode === 'lab_limited' ? undefined : () => navigate('/tax-invoices/create')}   /* 제한 모드 = 작성 버튼 숨김 (W6) */
    />
  );
}

function CreateTaxInvoiceWrapper({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromStatement = location.state?.fromStatement || null;
  return <CreateTaxInvoice user={user} onBack={() => navigate('/tax-invoices')} fromStatement={fromStatement} />;
}

// 관리자 잠금(PIN) 설정 Wrapper
function SecurityLockSettingsWrapper({ user }) {
  const navigate = useNavigate();
  return <SecurityLockSettings user={user} onBack={() => navigate('/settings')} />;
}

// 「외주 수주 설정」(/settings/outsource-vendor) 게이트 — S5(2026-10-27, 명세 §3-4): outsourceMf ON·베타면 바로 열고,
//   아니면 vendorProfiles/{uid} 를 1회 조회해 프로필(approved·review·ended 등)이 있으면 연다(가입 승인 때 서버가 만든 프로필을 플래그 OFF 상태에서도 관리할 수 있게).
//   userData 에 프로필 유무 필드가 없어 최소 침습으로 라우트 안에서 조회한다. 조회 실패는 console.error + 닫힘(/settings).
function VendorSettingsRoute({ features, userData, email, children }) {
  const type = userData?.businessType;
  const uid = userData?.uid;
  const flagOn = !!(features && isOutsourceMfOn(features, userData, userData?.email || email));
  const need = ['lab', 'outsource'].includes(type) && !flagOn && !!uid;
  const [has, setHas] = useState(null);   // null=조회 전, true/false=결과
  useEffect(() => {
    if (!need) return undefined;
    let alive = true;
    getVendorProfile(uid).then((p) => { if (alive) setHas(!!p); })
      .catch((e) => { console.error('[외주 수주 설정 게이트] 프로필 조회 실패 code=%s', e && e.code); if (alive) setHas(false); });
    return () => { alive = false; };
  }, [need, uid]);
  if (!['lab', 'outsource'].includes(type)) return <Navigate to="/settings" replace />;
  if (!features) return null;   // 플래그 로딩 중 (리다이렉트 깜빡임 방지)
  if (flagOn) return children;
  if (has === null) return null;   // 프로필 조회 중
  return has ? children : <Navigate to="/settings" replace />;
}

// 단계 미오픈 기능 라우트 가드 — 베타(아이반+연결 치과+관리자+해외)는 통과
// 모듈 레벨 정의 (App 렌더마다 새 컴포넌트 타입이 되어 서브트리가 리마운트되는 것 방지)
function PhaseRoute({ min, features, userData, email, children }) {
  if (!features) return null; // 플래그 로딩 중 (리다이렉트 깜빡임 방지)
  const beta = isBetaUser(features, userData, userData?.email || email);
  return (beta || (features.phase || 1) >= min) ? children : <Navigate to="/dashboard" replace />;
}

// 🎯 상용화 단계 라우트 가드 (2026-08-20 대표 확정 — docs/점검_상용화_기능단계_2026-08.md)
// 베타(아이반+연결 치과+관리자+해외)는 통과, 비베타는 config/features.stage >= min일 때만.
// 미달 시 안내 페이지 — 메뉴 숨김만으론 직접 URL 진입을 못 막아 라우트에서도 차단(점검 §6 B-4).
function StageClosedNotice() {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 520, margin: '80px auto', padding: '32px 24px', textAlign: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#334155' }}>{'이 기능은 곧 열립니다'}</div>
      <div style={{ fontSize: 14, color: '#64748b', marginTop: 10, lineHeight: 1.7 }}>
        {'지금은 준비 중인 화면입니다.'}<br />
        {'거래명세서 화면에서 오늘 업무를 이어가 주세요.'}
      </div>
      <button
        onClick={() => navigate('/transactions', { replace: true })}
        style={{ marginTop: 20, padding: '10px 20px', border: 'none', borderRadius: 8, background: '#6366F1', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        {'거래명세서로 이동'}
      </button>
    </div>
  );
}
function StageRoute({ min, features, userData, email, children }) {
  if (!features) return null; // 플래그 로딩 중 (리다이렉트 깜빡임 방지)
  return stageOpen(min, features, userData, userData?.email || email) ? children : <StageClosedNotice />;
}

// 🦷 치과 스캔 수집 라우트 가드 — 기능은 남기고 화면만 잠금.
// 판정은 isClinicScanIntakeOn 하나: clinicScanIntake 플래그 && (베타 || stage 2 이상) (2026-08-20 대표 확정)
function ScanIntakeRoute({ features, userData, email, children }) {
  const navigate = useNavigate();
  const on = isClinicScanIntakeOn(features, userData, userData?.email || email);
  useEffect(() => {
    if (!features || on) return;
    const t = setTimeout(() => navigate('/dashboard', { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [features, on, navigate]);
  if (!features) return null;   // 플래그 로딩 중 (깜빡임 방지)
  if (on) return children;
  return (
    <div style={{ maxWidth: 520, margin: '80px auto', padding: '32px 24px', textAlign: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#334155' }}>{'지금은 사용하지 않는 화면입니다'}</div>
      <div style={{ fontSize: 14, color: '#64748b', marginTop: 10, lineHeight: 1.7 }}>
        {'스캔 파일 수집 기능은 잠시 닫아두었습니다.'}<br />
        {'의뢰서는 [의뢰서 작성] 화면에서 바로 보내주세요.'}
      </div>
      <button
        onClick={() => navigate('/dashboard', { replace: true })}
        style={{ marginTop: 20, padding: '10px 20px', border: 'none', borderRadius: 8, background: '#6366F1', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        {'대시보드로 이동'}
      </button>
    </div>
  );
}

// 🔐 가입 승인 대기/거절 화면 (2026-08-25 대표 확정: 업체 가입은 관리자 승인 후 이용)
// 🗺️ 서버 알림 link '/clinic/patients?tab=answers'(계약_질문로드맵_웹 §6) → 실제 라우트 /patients 로(쿼리 보존). App() 안에서는 Router 밖이라 useLocation 불가 — 별도 컴포넌트
function LegacyClinicPatientsRedirect() {
  const location = useLocation();
  return <Navigate to={`/patients${location.search || ''}`} replace />;
}
// 📜 거절 사유 표시(S5, 법무 §6 ②·L12②): users.approvalRejectReason(관리자 callable adminApproveSignup 이 저장 — 서버 작업자)이 있으면 본인에게 보여준다(rules:143 본인 get 가능). v8.0 제4조 제2항 「거절 사유를 서비스 내에서 알립니다」 — 시행 전에도 표시(불리 변경 아님)
function ApprovalGateScreen({ status, rejectReason }) {
  const rejected = status === 'rejected';
  const reason = rejected ? String(rejectReason || '').trim() : '';
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f6f8', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e6e8ec', padding: '40px 32px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>{rejected ? '⚠️' : '⏳'}</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#151b26', margin: '0 0 10px' }}>
          {rejected ? '가입이 승인되지 않았습니다' : '가입 신청이 접수되었습니다'}
        </h2>
        <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.8, margin: '0 0 18px' }}>
          {rejected
            ? (reason
                ? `승인되지 않은 사유: ${reason}. 정정하여 다시 신청하려면 아래 연락처로 문의해 주세요.`
                : '가입 승인이 완료되지 않은 계정입니다. 자세한 내용은 아래 연락처로 문의해 주세요.')
            : '관리자 확인 후 이용하실 수 있습니다. 보통 1영업일 이내에 승인이 완료되며, 승인되면 바로 로그인하여 사용하실 수 있습니다.'}
        </p>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#374151', lineHeight: 1.9, marginBottom: 20, textAlign: 'left' }}>
          <b>문의</b><br />
          전화: 010-2972-3733<br />
          카카오톡: pf.kakao.com/_xfsREX
        </div>
        <button
          onClick={async () => {
            try { await signOut(auth); } catch (e) { console.error('로그아웃 실패:', e); }
            window.location.href = '/login';
          }}
          style={{ padding: '11px 28px', background: '#274690', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
        >
          로그아웃
        </button>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);

  // ── UTM 유입 추적: 첫 진입 URL의 utm_*을 세션에 보관 → 가입 시 signupVisits/users에 각인 (채널별 성과 측정) ──
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const utm = {};
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach(k => { const v = p.get(k); if (v) utm[k] = v; });
      if (Object.keys(utm).length > 0) {
        sessionStorage.setItem('dcUtm', JSON.stringify({ ...utm, landing: window.location.pathname }));
      }
    } catch (_) { /* 무시 */ }
  }, []);

  // ── 단계별 오픈 플래그 (config/features): phase 1=명세서, 2=해외수주, 3=치과 에이전트, 4=매칭 ──
  const [features, setFeatures] = useState(null);
  useEffect(() => {
    let alive = true;
    getFeatures().then(f => { if (alive) setFeatures(f); });
    return () => { alive = false; };
  }, [user?.uid]);
  // 📜 약관 v7.0 제4조의7 14항 — 시행(isTermsV70Effective) 후 치과 대표 문서 consents.delegation.version !== 'v7.0' 이면 환자관리 라우트 잠금.
  //   모달(재확인 기록)은 MainLayout 에 있고, 잠금 화면 버튼이 이벤트로 연다. 시행 전·치과 아님은 required:false (아무 영향 없음).
  const delegationV70 = useDelegationV70(features, userData);
  const dGate = (el) => <DelegationV70Gate {...delegationV70}>{el}</DelegationV70Gate>;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadUserData(firebaseUser.uid);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 사용자 데이터 로드 함수 (업체 가입 후 새로고침용)
  const loadUserData = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const freshData = {
          ...userDocSnap.data(),
          uid: uid,
          email: auth.currentUser?.email || '',
          // 관리자 커스텀 클레임 (화면 표시용 — 권한은 서버/rules, 보안 P0-3 2026-09-15)
          adminClaim: await fetchAdminClaim(auth.currentUser),
        };

        // 🔥🔥🔥 CRITICAL: 제거된 직원 비정상 상태 즉시 감지
        const hasStaffData = freshData.companyBusinessType || freshData.companyName;
        const hasNoCompany = !freshData.companyId;
        const isIndividual = freshData.userType === 'individual';

        // 비정상 상태: individual인데 회사 정보가 남아있음
        if (isIndividual && hasStaffData && hasNoCompany) {
          alert('계정 정보에 오류가 있어 수정합니다. 잠시만 기다려주세요.');

          // Firestore에서 완전히 삭제
          const { deleteField } = await import('firebase/firestore');
          await updateDoc(userDocRef, {
            businessType: deleteField(),
            companyId: deleteField(),
            companyName: deleteField(),
            companyBusinessType: deleteField(),
            businessName: deleteField(),
            role: deleteField(),
            staff: deleteField(),
            pendingCompanyId: deleteField(),
            pendingCompanyName: deleteField(),
            pendingCompanyType: deleteField()
          });

          // 깨끗한 데이터로 다시 로드
          const cleanSnapshot = await getDoc(userDocRef);
          const cleanData = {
            ...cleanSnapshot.data(),
            uid: uid,
            email: auth.currentUser?.email || '',
            adminClaim: freshData.adminClaim,
          };

          setUserData(cleanData);
          alert('계정 정보가 정상화되었습니다.');
          return cleanData;
        }

        // 구독 상태 확인 — "유효한 구독 or 유효한 체험"만 인정 (2026-07 유료 전환)
        // 기준: appSubscriptions에 (status가 'active' 또는 'trial') && expiresAt 존재 && expiresAt > now 문서가 있으면 true
        // expiresAt 없는 문서는 무효 (고아 레코드 무기한 이용 버그 수정)
        // 직원(companyId)은 대표(companyId) 기준으로 동일 판정 (구독 상속)
        //   단 본인 요금표(subscriptionTariffs/{본인 uid})가 있으면 companyId 가 있어도 본인 uid·본인 구독·본인 요금표로 판정
        //   (보안 P1-1, 서버 resolveOwner 와 같은 규칙 — billingPolicy.resolveBillingOwner 한 곳)
        // 약관 v6.2 제4조의2 ②·제4조의3 ②: 해지(cancelled)해도 이미 낸 기간(expiresAt)까지는 이용 — 해지 즉시 잠김 수정
        const billingOwner = await resolveBillingOwner({ ...freshData, uid });
        const billingOwnerUid = billingOwner.ownerUid || uid;
        // 10-15 로딩 개선: 구독 조회와 요금표 조회를 동시에 시작(종전 순차 2왕복). 각각의 실패 처리·판정 규칙은 그대로
        const tariffP = billingOwner.tariff === undefined
          ? loadTariff(billingOwnerUid).then(t => ({ t }), e => ({ e }))
          : Promise.resolve({ t: billingOwner.tariff });
        try {
          const subQuery = query(
            collection(db, 'appSubscriptions'),
            where('userId', '==', billingOwnerUid),
            where('status', 'in', ['active', 'trial', 'cancelled'])
          );
          const subSnap = await getDocs(subQuery);
          const now = new Date();
          // 판정 규칙은 billingPolicy.isLiveSub 한 곳 (expiresAt 없는 고아 문서 무효 + 결제 유예 BILLING_GRACE_DAYS=7일, active만 — 서버와 같은 규칙)
          const validSub = subSnap.docs.find(d => isLiveSub(d.data(), now));
          freshData.isSubscribed = !!validSub;
        } catch (e) {
          // 조회 실패로 유료 고객을 잠그지 않는다 — 열림 + 기록 (서버·앱·에이전트와 같은 fail-open, 2026-10-04)
          console.error('구독 상태 확인 실패 — 열림 처리(fail-open):', e);
          freshData.isSubscribed = true;
        }

        // 요금 판정표(계획_요금변경 §3·W2②): 청구 대상이 아니거나 무료기간 안이면 구독 상태로 본다.
        // - 요금표 없는 기존 국내 기공소 = 2026-12-31 23:59:59 KST까지 (trial 문서 만료일이 아니라 코드 상수로 보장)
        // - 면제(subscriptionExempt)·기존 치과·구독 대상 아닌 유형 = 서버가 청구하지 않으므로 화면도 잠그지 않음
        // - 요금표 있는 신규 = 체험 종료일까지
        // 요금표 유무 (판정 대상 uid 기준 — 본인 요금표 우선, 없으면 소속 업체) — 요금표가 있으면 해외(labKind) 단축 통과를 무시한다 (보안 P1-1, 서버 판정 순서: 요금표 → overseas).
        //   승인 대기 게이트에서도 쓰므로 화면용 필드로 남긴다. 읽기 실패는 기록하고 '없음'으로 둔다(서버도 읽기 실패 시 청구하지 않음).
        let ownerTariff;
        {
          const r = await tariffP;
          if (r.e) { console.error('요금표 읽기 실패:', r.e); ownerTariff = undefined; }
          else ownerTariff = r.t;
        }
        freshData.hasSubscriptionTariff = !!(ownerTariff && ownerTariff.code);

        // 🔐 시스템보안 P2-2(10-15): 직원 계정은 소속 업체(대표 문서)의 승인 상태도 본다 — 서버 decideWebAccess ownerPending 과 같은 규칙.
        //   직원 = companyId 가 본인 uid 와 다르고 본인 요금표가 없어 업체 기준으로 판정되는 계정(resolveBillingOwner.isStaff).
        //   업체가 승인 대기·거절이면 ownerApprovalStatus 를 남겨 아래 승인 게이트가 같은 화면을 보여 준다.
        //   해외(labKind=overseas) 업체는 제외하되, 업체 요금표가 있으면 제외하지 않는다(서버 pendingOf 와 같음).
        //   업체 문서를 읽지 못하면 기존 동작(통과)을 유지하고 기록만 남긴다.
        freshData.ownerApprovalStatus = null;
        if (billingOwner.isStaff && freshData.companyId) {
          try {
            const ownerSnap = await getDoc(doc(db, 'users', freshData.companyId));
            const owner = ownerSnap.exists() ? ownerSnap.data() : null;
            if (owner && (owner.approvalStatus === 'pending' || owner.approvalStatus === 'rejected')
                && (owner.labKind !== 'overseas' || !!(ownerTariff && ownerTariff.code))) {
              freshData.ownerApprovalStatus = owner.approvalStatus;
            }
          } catch (e) {
            console.error('소속 업체 승인 상태 확인 실패 — 기존 동작 유지(통과):', e?.code || e?.message);
          }
        }

        let billingDecision = null;
        if (!freshData.isSubscribed) {
          try {
            billingDecision = await loadBillingDecision({ ...freshData, uid }, ownerTariff === undefined ? { resolved: billingOwner } : { resolved: billingOwner, tariff: ownerTariff });
            if (isFreeNow(billingDecision)) freshData.isSubscribed = true;
          } catch (e) {
            console.error('요금 판정 실패:', e);
          }
        }

        // 베타(아이반 파일럿·연결 치과)·관리자·해외 파일럿은 구독 없이 이용 (config/features 기반)
        // 단 요금표가 있는 회원은 해외(labKind) 단축 통과 제외 — labKind 는 본인이 고칠 수 있는 필드
        if (!freshData.isSubscribed) {
          try {
            const features = await getFeatures();
            const ignoreOverseas = freshData.hasSubscriptionTariff || decisionHasTariff(billingDecision);
            if (isBetaUser(features, freshData, freshData.email, { ignoreOverseas })) {
              freshData.isSubscribed = true;
            }
          } catch (e) {
            console.error('베타 판정 실패:', e);
          }
        }

        // 접근 모드 (계획_가입승인결제흐름 §3-3 · W3) — 위 "열림" 판정(isSubscribed)을 그대로 받아 모드만 붙인다.
        //   allFree=true(paywallActive false)면 항상 full. 설정 읽기 실패·판정 실패는 full(fail-open, 기록은 각 단계가 남김).
        try {
          const featuresForMode = await getFeatures();
          freshData.accessMode = featuresForMode?._readFailed === true
            ? ACCESS_MODES.FULL
            : computeAccessMode({ user: freshData, features: featuresForMode, isSubscribed: freshData.isSubscribed === true, tariff: ownerTariff, isAdmin: isAdminUser(freshData) });
        } catch (e) {
          console.error('접근 모드 판정 실패 — 열림(full) 처리:', e);
          freshData.accessMode = ACCESS_MODES.FULL;
        }

        setUserData(freshData);

        // 방문 기록 저장 — 10-15 로딩 개선: 화면을 열기 전에 기다리지 않는다(실패는 기록)
        try {
          addDoc(collection(db, 'visitLogs'), {
            userId: uid,
            userType: freshData.userType || 'unknown',
            businessType: freshData.businessType || freshData.companyBusinessType || '',
            platform: 'web',
            visitedAt: serverTimestamp(),
            date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
          }).catch(e => console.error('방문 기록 저장 실패:', e?.code || e?.message));
        } catch (e) { console.error('방문 기록 저장 실패:', e?.code || e?.message); }

        return freshData;
      } else {
        const basicData = {
          uid: uid,
          email: auth.currentUser?.email || '',
          adminClaim: await fetchAdminClaim(auth.currentUser),
        };
        setUserData(basicData);
        return basicData;
      }
    } catch (error) {
      console.error('사용자 데이터 로드 실패:', error);
      const errorData = {
        uid: uid,
        email: auth.currentUser?.email || ''
      };
      setUserData(errorData);
      return errorData;
    }
  };

  // 🆕 강제 새로고침 함수 (MainLayout에서 사용)
  const refreshUserData = async () => {
    if (user?.uid) {
      return await loadUserData(user.uid);
    }
    return null;
  };

  // CompanyRegister에서 사용할 수 있도록 window 객체에 추가
  useEffect(() => {
    window.refreshUserData = refreshUserData;
    return () => {
      delete window.refreshUserData;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>로딩 중...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // 로그인하지 않은 경우
  if (!user) {
    // 🆓 공개 페이지 목록 (비회원도 접근 가능)
    const publicPaths = ['/find-lab', '/find-clinic', '/clinic/', '/clinic-profile/', '/signup', '/login', '/invite', '/promo', '/lab', '/review', '/banner', '/privacy', '/terms', '/location-terms', '/patient', '/requests', '/gallery', '/test-cases', '/as', '/global', '/join', '/s/', '/agent-embed', '/p/', '/account/delete', '/walkin/', '/care/'];   // '/account/delete' = 계정 삭제 안내(스토어 필수, 공개). '/p/' = 환자 QR 착지·앱 안내(웹 등록 없음). 슬래시 필수 — '/p'면 /patients·/privacy·/promo까지 공개됨
    // 참고: '/find-clinic-info'도 '/find-clinic' prefix 매칭으로 자동 포함됨
    const currentPath = window.location.pathname;
    // 루트("/")는 비회원 공개 홈으로 — 처음 온 사람이 로그인 없이 둘러볼 수 있게
    const isPublicPath = currentPath === '/' || publicPaths.some(path => currentPath.startsWith(path));

    // 공개 페이지면 Router 렌더링
    if (isPublicPath) {
      return (
        <Router>
          <Routes>
            <Route path="/" element={<PublicHome />} />
            <Route path="/requests" element={<PublicRequests />} />
            <Route path="/gallery" element={<PublicGallery />} />
            <Route path="/find-lab" element={<PublicLabDirectory user={null} />} />
            <Route path="/find-clinic" element={<PublicClinicDirectory />} />
            <Route path="/clinic-profile/:hospitalId" element={<ClinicProfile />} />
            <Route path="/clinic/welcome" element={<ClinicWelcome />} />
            {/* 🏭 기공소용 랜딩 (비회원, 배너/전단 QR 착지점) — 동적 /lab/:labId보다 먼저, exact 매칭 */}
            <Route path="/lab" element={<LabLanding />} />
            <Route path="/lab/:labId" element={<LabProfile user={null} />} />
            {/* 📞 A/S 무료제작 신청 랜딩 (비회원, 가입 없음) */}
            <Route path="/as" element={<AsLanding />} />
            {/* 🦷 테스트보철 공개 게시판 — 4차(매칭) 오픈 전까지 비회원 비공개 (수락 링크는 로그인 유도) */}
            <Route path="/test-cases" element={<Navigate to="/login" replace />} />
            {/* 상세는 공개 유지 — A/S 컨시어지 수락 링크(관리자→기공소 전달)의 착지점 */}
            <Route path="/test-cases/:requestId" element={<TestCaseDetail user={null} />} />
            {/* 🔗 명세서 공유 링크 열람 (비회원 치과, 토큰 URL) */}
            <Route path="/s/:token" element={<StatementShareView />} />
            {/* 🦷 환자 QR 착지 — 앱 전용 안내(치과명+스토어+앱에서 열기). 웹 등록 없음 (2026-09-10 저녁 대표 확정) */}
            <Route path="/p/c/:token" element={<PatientQrLanding />} />
            <Route path="/p/app" element={<PatientQrLanding />} />
            <Route path="/p/*" element={<Navigate to="/p/app" replace />} />
            {/* 🪪 웹 접수 — 치과 데스크 QR 착지 (비회원, 2026-10-01). 슬러그는 치과 식별값, 환자 식별정보 없음 */}
            <Route path="/walkin/:slug" element={<ClinicWalkin />} />
            <Route path="/signup" element={<PublicRegisterWrapper onRegister={(user) => setUser(user)} />} />
            {/* 🌏 해외 기공소 랜딩(홍보 주소) + 가입 (영문, 오픈 가입 + 관리자 승인) */}
            <Route path="/global" element={<GlobalLanding />} />
            <Route path="/join/global" element={<JoinGlobal />} />
            <Route path="/login" element={<PublicLoginWrapper onLogin={(user) => setUser(user)} />} />
            {/* 🖥️ 덴클 프로그램(에이전트 0.9.1) 임베드 진입점 — 커스텀 토큰 자동 로그인 */}
            <Route path="/agent-embed" element={<AgentEmbedEntry onLogin={(user) => setUser(user)} />} />
            {/* 🆕 아이반치과기공소 프로모션 랜딩페이지 (비회원 접근 가능) */}
            <Route path="/promo/ivan" element={<IvanPromoLanding />} />
            {/* 🆕 기공소용 프로모션 랜딩페이지 (비회원 접근 가능) */}
            <Route path="/promo/lab" element={<LabPromoLanding />} />
            <Route path="/promo/lab-stats" element={<LabPromoStats />} />
            <Route path="/promo/lab-recruit" element={<LabRecruitLanding />} />
            {/* 🎨 디자인 외주 마켓 홍보 랜딩 — 대문(PublicHome)과 별개, 대표가 홍보 링크로만 사용 (2026-08-24 대표 지시) */}
            <Route path="/promo/design" element={<DesignMarketPromo />} />
            <Route path="/banner/lab" element={<BannerLab />} />
            {/* 🆕 환자 앱 다운로드 유도 페이지 (마케팅 광고 진입점) */}
            <Route path="/patient" element={<PatientAppLanding />} />
            <Route path="/patient/terms" element={<PatientTerms />} />
            {/* 계정 삭제 안내 — 스토어 필수 공개 페이지 (전체지도 P1-1). 환자=앱 설정 › 계정 삭제 / 업체=이메일 접수 */}
            <Route path="/account/delete" element={<AccountDeleteGuide />} />
            <Route path="/care/terms" element={<CareTerms />} />
            <Route path="/care/account-delete" element={<CareAccountDelete />} />
            <Route path="/patient/privacy" element={<PatientPrivacy />} />
            <Route path="/privacy" element={<PrivacyPolicy onClose={() => window.history.back()} />} />
            <Route path="/terms" element={<TermsOfService onClose={() => window.history.back()} />} />
            {/* 위치기반서비스 이용약관 (가입 필수 동의 폐지 후 공개 게시용, 2026-08-06) */}
            <Route path="/location-terms" element={<LocationTerms onClose={() => window.history.back()} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      );
    }

    // 그 외: Login 또는 Register 표시 (Router로 감싸서 useNavigate 사용 가능하게)
    return (
      <Router>
        {showRegister ? (
          <Register
            onRegister={(user) => setUser(user)}
            onShowLogin={() => setShowRegister(false)}
            onClose={() => setShowRegister(false)}
            onSuccess={() => setShowRegister(false)}
          />
        ) : (
          <Login
            onLogin={(user) => setUser(user)}
            onShowRegister={() => setShowRegister(true)}
          />
        )}
      </Router>
    );
  }

  // 🔐 가입 승인제 게이트 (2026-08-25 대표 확정: 가입 후 관리자 승인 전 사용 제한)
  //    - 필드 없음 = 승인됨 (배포 이전 기존 회원이 잠기지 않게 하는 원칙 — 모든 판정에 동일 적용)
  //    - 직원은 자기 문서 기준: 직원 문서에는 approvalStatus가 없으므로 통과 (회사 승인 체계 별도)
  //    - 해외 기공소(labKind=overseas)는 기존 globalStatus(pending_review→active) 승인 화면(/global/status, 영문)이 담당하므로 제외
  //    - 공개 문서·프로모 라우트만 게이트 밖 (약관·개인정보는 대기 중에도 열람 가능해야 함)
  //    - 단 요금표(subscriptionTariffs)가 있는 회원은 해외 제외를 적용하지 않는다 — 요금표는 국내 가입에만 찍히므로
  //      labKind(본인 수정 가능)를 'overseas'로 바꿔 승인 대기를 빠져나가는 것 차단 (보안 P1-1)
  //    - 🔐 시스템보안 P2-2(10-15): 직원은 소속 업체 승인 상태(ownerApprovalStatus, loadUserData 에서 판정)가 대기·거절이면 같은 화면
  const ownApprovalLocked = !!userData && (userData.labKind !== 'overseas' || userData.hasSubscriptionTariff === true) &&
      (userData.approvalStatus === 'pending' || userData.approvalStatus === 'rejected');
  const ownerApprovalLocked = !!userData && (userData.ownerApprovalStatus === 'pending' || userData.ownerApprovalStatus === 'rejected');
  if (ownApprovalLocked || ownerApprovalLocked) {
    return (
      <Router>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy onClose={() => window.history.back()} />} />
          <Route path="/terms" element={<TermsOfService onClose={() => window.history.back()} />} />
          <Route path="/location-terms" element={<LocationTerms onClose={() => window.history.back()} />} />
          <Route path="/promo/ivan" element={<IvanPromoLanding />} />
          <Route path="/promo/lab" element={<LabPromoLanding />} />
          <Route path="/promo/lab-stats" element={<LabPromoStats />} />
          <Route path="/promo/lab-recruit" element={<LabRecruitLanding />} />
          <Route path="/promo/design" element={<DesignMarketPromo />} />
          <Route path="*" element={<ApprovalGateScreen status={ownApprovalLocked ? userData.approvalStatus : userData.ownerApprovalStatus} rejectReason={ownApprovalLocked ? userData.approvalRejectReason : ''} />} />
        </Routes>
      </Router>
    );
  }

  // 🦷 환자 계정 분기 (2026-09-10 환자관리) — 환자 uid로 로그인되면 업체 셸(MainLayout)로 보내지 않는다.
  //   userType==='patient'(환자앱·kakaoLogin CF가 심음) 또는 users 문서가 아직 없는 uid(/p/ 경로에서만).
  //   웹 등록 폐지(09-10 저녁) 후 착지는 앱 안내(/p/app)뿐 — 웹에 환자 화면(/p/me)은 없다.
  //   업체 계정(businessType·companyId·staff)이 /p/ 경로를 열면 차단 안내만 (실사 §3: 별도 Auth 인스턴스 대신 안내).
  {
    const isBusinessAccount = !!(userData && (userData.businessType || userData.companyId || userData.companyBusinessType
      || (userData.userType && userData.userType !== 'patient')));
    const onPatientPath = window.location.pathname.startsWith('/p/');
    if (userData && !isBusinessAccount && (userData.userType === 'patient' || onPatientPath)) {
      return (
        <Router>
          <Routes>
            <Route path="/p/c/:token" element={<PatientQrLanding />} />
            <Route path="/p/app" element={<PatientQrLanding />} />
            <Route path="/privacy" element={<PrivacyPolicy onClose={() => window.history.back()} />} />
            <Route path="/terms" element={<TermsOfService onClose={() => window.history.back()} />} />
            <Route path="/care/terms" element={<CareTerms />} />
            <Route path="/care/account-delete" element={<CareAccountDelete />} />
            <Route path="*" element={<Navigate to="/p/app" replace />} />
          </Routes>
        </Router>
      );
    }
    if (isBusinessAccount && onPatientPath) {
      return (
        <Router>
          <Routes>
            <Route path="/p/*" element={<PatientShell blocked />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      );
    }
  }

  // 서류(명세서·계산서·정산) 라우트에서 차단할 직원 여부 — 기공소·외주 직원만 차단, 치과 직원은 개방 (2026-08-05 대표 확정)
  const isDocBlockedStaff = !!(userData?.companyId && ['lab', '기공소', 'outsource'].includes(userData?.companyBusinessType || ''));
  // 대표 10-15: 치과 「정산 확인」(/clinic/settlement)·「받은 계산서」(/tax-invoices) 숨김 — 나중에 작업할 때 다시 연다(「받은 명세서」는 같은 날 다시 표시). 기공소 세금계산서(/tax-invoices)는 그대로
  const isClinicAccount = ['dental', 'clinic', '치과'].includes(userData?.businessType || userData?.companyBusinessType || '');

  // 구 환자앱 폐기(2026-09-14): 구 환자앱 전제 화면 라우트는 직접 진입 시 신 환자관리(/patients) 또는 /clinic-today로 보냄 (플래그 무관).
  // 화면 컴포넌트·import는 보존 — 폐기 계획 승인 후 제거 (docs/실사_구환자앱_폐기_2026-09-14.md §3)
  const isClinicAcct = ['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType);
  const legacyPatientTarget = (isClinicAcct && isClinicPatientsOn(features, userData, userData?.email || user?.email)) ? '/patients' : '/clinic-today';
  const legacyPatientRedirect = <Navigate to={legacyPatientTarget} replace />;

  // 로그인한 경우: 메인 앱 표시
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* 인증 라우트 */}
          <Route
            path="/login"
            element={
              user ? <Navigate to={
                userData?.labKind === 'overseas' ? '/global/status'
                : (userData?.businessType === 'seller' || userData?.sellerStatus === 'approved') ? '/seller-dashboard'
                : userData?.businessType === 'outsource' ? (isPartnerHomeOn(features, userData) ? '/partner' : '/outsource-request')   /* 🏭 제조사 → CNC 파트너 홈은 partnerHome 스위치 ON 때만 (10-23) */
                : ((userData?.userType === 'staff' || userData?.userType === 'individual') && !userData?.companyId) ? '/staff-invitations'
                /* 결제 전 기공소(제한 모드) → 받은 의뢰서 (계획_가입승인결제흐름 W3) */
                : userData?.accessMode === 'lab_limited' ? '/orders'
                /* 1차 비베타 기공소 → 거래명세서 ("/" 기본 리다이렉트와 동일 착지) */
                : ((userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab') && features && !isBetaUser(features, userData, userData?.email || user?.email)) ? '/transactions'
                /* 치과(대표·직원, 베타 무관) → 대시보드 (2026-08-07 대표 확정: 비베타 치과 대시보드 개방) */
                : '/dashboard'
              } /> : <PublicLoginWrapper onLogin={(user) => setUser(user)} />
            }
          />
          <Route
            path="/signup"
            element={
              user ? <Navigate to={
                (userData?.businessType === 'seller' || userData?.sellerStatus === 'approved') ? '/seller-dashboard'
                : userData?.businessType === 'outsource' ? (isPartnerHomeOn(features, userData) ? '/partner' : '/outsource-request')   /* 🏭 제조사 → CNC 파트너 홈은 partnerHome 스위치 ON 때만 (10-23) */
                : ((userData?.userType === 'staff' || userData?.userType === 'individual') && !userData?.companyId) ? '/staff-invitations'
                : userData?.businessType === 'lab' ? '/transactions'   /* 기공소 가입 직후 → 거래명세서 (1차 슬림 메뉴 기준. 4차 매칭 오픈 시 /test-cases 복원) */
                : (userData?.businessType === 'dental' && sessionStorage.getItem('clinicInviteSignup') === '1') ? '/create-order'   /* 초대 가입 치과 → 첫 의뢰서 작성 유도 */
                : '/dashboard'
              } /> : <PublicRegisterWrapper onRegister={(user) => setUser(user)} />
            }
          />

          {/* 🖥️ 덴클 프로그램(에이전트 0.9.1) 임베드 진입점 — 이미 로그인 상태면 재로그인 생략 후 dest로 이동 */}
          <Route path="/agent-embed" element={<AgentEmbedEntry />} />

          {/* 🆓 공개 기공소 찾기 (비회원·회원 분기)
              - 비회원: 풀스크린 (외부 공유·마케팅 진입점)
              - 회원: 사이드바 안 /clinic/find-lab 으로 자동 리다이렉트 */}
          <Route
            path="/find-lab"
            element={user ? <Navigate to="/clinic/find-lab" replace /> : <PublicLabDirectory user={null} />}
          />

          {/* 🆓 공개 치과 찾기 (전국 19,596개 치과) */}
          <Route
            path="/find-clinic"
            element={<PublicClinicDirectory />}
          />

          {/* 🏭 기공소용 랜딩 — 로그인 상태에서는 대시보드로 (이미 가입한 사용자에게 랜딩 불필요) */}
          <Route path="/lab" element={<Navigate to="/dashboard" replace />} />

          {/* 통계 페이지 (동적 :labId보다 먼저 정의) */}
          <Route path="/lab/visitor-stats" element={<LabVisitorStats user={userData} />} />
          <Route path="/lab/promo-settings" element={userData?.companyId ? <Navigate to="/settings" replace /> : <LabPromoSettings user={userData} />} />

          {/* 🆕 기공소 프로필 페이지 (공개) */}
          <Route
            path="/lab/:labId"
            element={<LabProfile user={userData} />}
          />



          {/* 기공소 찾기 (치과용) */}
          <Route
            path="/lab-directory"
            element={
              user ? (
                <MainLayout user={userData} currentPage="lab-directory" refreshUserData={refreshUserData}>
                  <LabDirectory user={userData} />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* 기공소 홍보 (기공소 대표만, 직원 접근 차단) */}
          <Route
            path="/lab-advertising"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : userData?.companyId ? (
                <Navigate to="/settings" replace />
              ) : (
                <PhaseRoute min={4} features={features} userData={userData} email={user?.email}>
                  <MainLayout user={userData} currentPage="lab-advertising" refreshUserData={refreshUserData}>
                    <LabAdvertising user={userData} />
                  </MainLayout>
                </PhaseRoute>
              )
            }
          />

          {/* 외주업체 홍보 (외주업체용) */}
          <Route
            path="/outsource-advertising"
            element={
              user ? (
                <MainLayout user={userData} currentPage="outsource-advertising" refreshUserData={refreshUserData}>
                  <OutsourceAdvertising user={userData} />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* 📞 A/S 랜딩 — 로그인 상태에서도 풀스크린 */}
          <Route path="/as" element={<AsLanding />} />
          {/* 🪪 웹 접수 — 로그인 상태(치과 PC 테스트 등)에서도 풀스크린 (2026-10-01) */}
          <Route path="/walkin/:slug" element={<ClinicWalkin />} />
          {/* 🔔 원장 호출 전체 화면 (2026-10-08) — 진료실 태블릿 "홈 화면에 추가"용이라 사이드메뉴 없이(MainLayout 밖). ?view=doctor = 원장 응답 보기.
              치과(대표·직원)만, doctorCall 플래그 strict(베타 미리보기 없음). 비로그인은 공개 경로가 아니라 로그인 화면으로 간다.
              환자 정보가 없는 화면이라 dGate(약관 v7.0 위탁 재확인)는 걸지 않는다. features 로딩 전(null)에는 대기(/reception 과 같은 이유) */}
          <Route path="/call" element={
            features === null ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', color: '#64748b' }}>
                <div style={{ ...styles.spinner, width: 40, height: 40, marginBottom: 16 }}></div>
                <p style={{ fontSize: 14, margin: 0 }}>{'불러오는 중...'}</p>
                <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
              </div>
            ) : ((['clinic', 'dental', '치과'].includes(userData?.businessType) || ['clinic', 'dental', '치과'].includes(userData?.companyBusinessType)) && isDoctorCallOn(features)
                && !['pending', 'rejected'].includes(userData?.approvalStatus))   /* 승인 대기·거절 치과 비노출(rules 와 같은 기준 — 대표는 위 승인 게이트가 먼저 막지만 명시) */
              ? <DoctorCallScreen user={userData} exitPath={isDeskReceptionOn(features) ? '/reception' : '/dashboard'} /> : <Navigate to="/dashboard" replace />
          } />
          {/* 프로모 페이지 — MainLayout 없이 렌더링 (로그인 사용자도 사이드바 없이) */}
          <Route path="/promo/lab-stats" element={<LabPromoStats />} />
          <Route path="/promo/lab" element={<LabPromoLanding />} />
          <Route path="/promo/lab-recruit" element={<LabRecruitLanding />} />
          <Route path="/promo/design" element={<DesignMarketPromo />} />
          {/* 🔗 명세서 공유 링크 열람 — 로그인 상태에서도 공개 블록(749줄)처럼 MainLayout 밖에 (2026-10-24 재점검 P2-17: 치과 종이에 사이드바가 붙지 않게) */}
          <Route path="/s/:token" element={<StatementShareView />} />

          {/* 보호된 라우트 - MainLayout으로 감싸기 */}
          <Route
            path="/*"
            element={
              user ? (
                <MainLayout user={userData} refreshUserData={refreshUserData}>
                  <Routes>
                    {/* 메인 기능 */}
                    {/* 치과(대표·직원)는 대시보드 폐지 → 「오늘」로 통합 (대표 승인 2026-08-18: 카드 보드 제거 ①안).
                        로그인 착지·각종 가드의 /dashboard 진입이 전부 여기서 /clinic-today로 모인다. 기공소는 종전대로 */}
                    <Route path="/dashboard" element={
                        userData?.labKind === 'overseas' ? <Navigate to="/global/status" replace />
                        : (['clinic', 'dental', '치과'].includes(userData?.businessType) || ['clinic', 'dental', '치과'].includes(userData?.companyBusinessType)) ? <Navigate to="/clinic-today" replace />
                        : <Dashboard user={userData} />
                    } />

                    {/* 주문 작성 - 모든 사업자 계정 접근 가능 */}
                    <Route path="/create-order" element={<CreateOrder user={userData} />} />

                    {/* 외주주문 */}

                    {/* 채팅 */}
                    <Route path="/chat" element={<ChatRoom user={userData} />} />

                    <Route path="/orders" element={<OrderList user={userData} />} />

                    {/* 제작 현황 보드 (2026-08-05 대표 확정) — 기공소·치과 대표/직원 모두 접근 (핀 구역 아님, 기술자가 밀링 체크) */}
                    <Route path="/production" element={
                        ['lab', '기공소', 'clinic', 'dental', '치과'].includes(userData?.businessType || '') ||
                        ['lab', '기공소', 'clinic', 'dental', '치과'].includes(userData?.companyBusinessType || '')
                            ? <ProductionBoard user={userData} />
                            : <Navigate to="/orders" replace />
                    } />

                    {/* 공정 체크 화면 임시 확인용 (메뉴 노출 없음, config/features.qrCheck=true 일 때만 열림) */}
                    <Route path="/case-check-preview" element={<CaseCheckPreview />} />

                    {/* QR 공정 체크 착지 (기공소 전용 — 의뢰서 인쇄물의 QR이 여기로 온다, 2026-08-18) */}
                    <Route path="/q/:token" element={<CaseCheckPage user={userData} />} />

                    {/* ✨ ViewOrder 라우트 - 경로 수정 */}
                    <Route
                      path="/view-order/:orderId"
                      element={<ViewOrderWrapper user={{ ...user, ...userData }} />}
                    />

                    {/* 메시지 보기 (이메일 파싱된 메시지) */}
                    <Route
                      path="/view-message/:orderId"
                      element={<ViewMessage user={{ ...user, ...userData }} />}
                    />

                    <Route path="/calendar" element={<CalendarView user={userData} />} />
                                        <Route path="/marketplace" element={<MarketplaceLayout><Marketplace user={userData} /></MarketplaceLayout>} />
                    <Route path="/naver-review" element={<NaverReviewManager user={userData} />} />
                    <Route path="/profile-ad" element={<ProfileAdManager user={userData} />} />
                    <Route path="/checkout" element={<Checkout user={user} userInfo={userData} />} />
                    {/* 판매자 기능 */}
                    <Route
                      path="/seller/product-management"
                      element={<MarketplaceProductManagement user={userData} />}
                    />
                    <Route
                      path="/seller-application"
                      element={<SellerApplication userInfo={userData} />}
                    />
                    <Route
                      path="/seller-application-status"
                      element={<SellerApplicationStatus userInfo={userData} />}
                    />
                    <Route
                      path="/seller-dashboard"
                      element={<SellerDashboard userInfo={userData} />}
                    />
                    <Route
                      path="/product-registration"
                      element={<ProductRegistration userInfo={userData} />}
                    />
                    <Route path="/product-edit/:id" element={<ProductEdit userInfo={userData} />} />
                    <Route path="/order-management" element={<OrderManagement userInfo={userData} />} />
                    <Route path="/product/:id" element={<ProductDetail userInfo={userData} />} />
                    <Route
                      path="/product-management"
                      element={<ProductManagement userInfo={userData} />}
                    />
                    <Route
                      path="/bulk-product-upload"
                      element={<BulkProductUpload userInfo={userData} />}
                    />
                    <Route
                      path="/seller-orders"
                      element={<SellerOrderList userInfo={userData} />}
                    />
                    <Route
                      path="/seller-settlement"
                      element={<SellerSettlement userInfo={userData} />}
                    />
                    <Route
                      path="/seller-store-settings"
                      element={<SellerStoreSettings userInfo={userData} />}
                    />
                    {/* 🆕 재료상 치과 홍보 링크 관리 */}
                    <Route
                      path="/seller-referral"
                      element={<SellerReferralLink userInfo={userData} />}
                    />
                    <Route
                      path="/marketplace/seller/:sellerId"
                      element={<MarketplaceLayout><SellerStorefront user={userData} /></MarketplaceLayout>}
                    />
                    <Route
                      path="/admin/seller-approval"
                      element={<AdminSellerApproval userInfo={userData} />}
                    />
                    <Route
                      path="/admin/product-approval"
                      element={<AdminProductApproval userInfo={userData} />}
                    />

                    {/* 장바구니 & 주문 */}
                    <Route path="/cart" element={<MarketplaceLayout><Cart user={user} userInfo={userData} /></MarketplaceLayout>} />
                    <Route path="/order-complete" element={<OrderComplete />} />

                    {/* 중고물품 */}
                    <Route path="/used-items" element={<UsedItemsList />} />
                    <Route path="/used-items/register" element={<UsedItemRegistration />} />
                    <Route path="/used-items/:itemId" element={<UsedItemDetail />} />

                    {/* 설정 메뉴 (일부 PIN 보호) */}
                    <Route path="/profile" element={<Profile user={userData} />} />
                    <Route path="/connections" element={<ConnectionList user={userData} />} />
                    <Route path="/transactions" element={isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><ClinicSubGate user={userData} title="받은 명세서" lockedRender={() => <TransactionStatementList user={userData} readOnly />}><TransactionStatementList user={userData} /></ClinicSubGate></SensitiveGate>} />   {/* 결제하지 않은 치과 = 잠금 대신 읽기 전용 (대표 10-15 "응") */}
                    {/* 수집함 1급 메뉴 (2026-07-24 사이드메뉴 개편 B안) — 명세서 목록의 inbox 뷰로 바로 진입 */}
                    <Route path="/inbox" element={(userData?.userType === 'staff' || userData?.companyId) ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><TransactionStatementList user={userData} initialView="inbox" /></SensitiveGate>} />
                    <Route path="/partners" element={(userData?.userType === 'staff' || userData?.companyId) ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><PartnerOverview user={userData} /></SensitiveGate>} />
                    <Route path="/tax-invoices" element={isClinicAccount ? <Navigate to="/orders" replace /> : isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><ClinicSubGate user={userData} title="받은 계산서"><TaxInvoiceListWrapper user={userData} /></ClinicSubGate></SensitiveGate>} />
                    <Route path="/tax-invoices/create" element={userData?.accessMode === 'lab_limited' ? <Navigate to="/subscription" replace /> : isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><CreateTaxInvoiceWrapper user={userData} /></SensitiveGate>} />
                    <Route path="/tax-invoices/:id" element={isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><TaxInvoiceDetailWrapper user={userData} /></SensitiveGate>} />

                    <Route path="/statistics" element={<SensitiveGate user={userData}><Statistics user={userData} /></SensitiveGate>} />

                    {/* 🆕 메뉴 통합 Hub 라우트 */}
                    <Route path="/schedule" element={<ScheduleHub user={userData} />} />
                    <Route path="/statements" element={(userData?.userType === 'staff' || userData?.companyId) ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><StatementsHub user={userData} /></SensitiveGate>} />
                    <Route path="/security-lock" element={<SecurityLockSettingsWrapper user={userData} />} />
                    <Route path="/stats" element={<SensitiveGate user={userData}><StatisticsHub user={userData} /></SensitiveGate>} />
                    <Route path="/settings" element={<SettingsHub user={userData} userData={userData} />} />
                    {/* 🦷 치과 초대 관리 (features.clinicInvite 플래그로 내부 게이팅) */}
                    <Route path="/clinic-invites" element={<ClinicInvites user={userData} />} />
                    <Route path="/settings/payment" element={<PaymentSettings user={userData} />} />
                    {/* 🖥️ 접속 PC 관리 — 원격 로그아웃 1단계 (2026-08-06). /settings 하위 = 기공소 대표 PIN 구역 */}
                    <Route path="/settings/devices" element={<AgentDevices user={userData} />} />
                    <Route path="/settings/clinic-progress" element={<ClinicProgressShare user={userData} />} />
                    <Route path="/settings/intake-fields" element={<ClinicIntakeFields user={userData} />} />
                    {/* 🩺 장착 후 확인 설정 (2026-10-02, 플래그 patientFeedback — 페이지 안에서 게이트) */}
                    <Route path="/settings/feedback" element={dGate(<ClinicFeedbackSettings user={userData} />)} />
                    {/* 🗺️ 진료 후 질문 설정 (2026-10-06, 플래그 roadmapEngine — 페이지 안에서 게이트, 저장은 서버가 대표만 허용) */}
                    <Route path="/settings/roadmap" element={dGate(<ClinicRoadmapSettings user={userData} />)} />
                    {/* 🪪 웹 접수 QR (2026-10-01, 플래그 clinicWalkin — 페이지 안에서 게이트) */}
                    <Route path="/settings/walkin-qr" element={dGate(<ClinicWalkinQr user={userData} />)} />
                    {/* 🛡️ 환자 정보 처리위탁 (2026-10-08) — 확인·철회 화면이라 dGate(약관 v7.0 재확인 잠금) 밖에 둔다(철회는 언제나 가능해야 함). 치과 아님은 페이지 안에서 안내 */}
                    <Route path="/settings/clinic-delegation" element={<ClinicDelegationSettings user={userData} />} />
                    {/* 🦷 환자 공개 범위 (2026-10-14) — 끄는 쪽(공개 축소)은 언제나 가능해야 해서 dGate 밖. 플래그·치과 여부는 페이지 안에서 게이트, 저장은 서버가 대표만 허용 */}
                    <Route path="/settings/patient-share" element={<ClinicPatientShareSettings user={userData} />} />
                    {/* 🔔 원장 호출 설정 (2026-10-08, 플래그 doctorCall — 페이지 안에서 게이트, 저장은 rules 가 대표만 허용) */}
                    {/* features 로딩 전(null)은 페이지가 '불러오는 중'으로 대기, 로딩 후 플래그 OFF·비치과·승인 대기/거절이면 /settings 로 */}
                    <Route path="/settings/doctor-call" element={
                      (features === null || ((['clinic', 'dental', '치과'].includes(userData?.businessType) || ['clinic', 'dental', '치과'].includes(userData?.companyBusinessType))
                        && isDoctorCallOn(features) && !['pending', 'rejected'].includes(userData?.approvalStatus)))
                        ? <DoctorCallSettings user={userData} /> : <Navigate to="/settings" replace />
                    } />
                    <Route path="/business-analysis" element={<SensitiveGate user={userData}><BusinessAnalysis user={userData} /></SensitiveGate>} />
                    <Route path="/remake-analysis" element={<ClinicSubGate user={userData} title="재제작 분석"><RemakeAnalysis user={userData} /></ClinicSubGate>} />
                    {/* 기공비 분석 — 치과 원장용 (2026-08-09). 민감구역 가드 재사용(핀) */}
                    {/* 2026-10-12 대표 결정: 치과 대표 전용 — 직원은 /dashboard 로(서버 getLabCostAnalysis 도 owner-only 거부, 판정 기준 동일) */}
                    <Route path="/lab-cost" element={(userData?.userType === 'staff' || (userData?.companyId && userData.companyId !== userData?.uid)) ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><ClinicSubGate user={userData} title="기공비 분석"><LabCostAnalysis user={userData} /></ClinicSubGate></SensitiveGate>} />
                    {/* 재택 작업 관제 — 기공소 (2026-08-09). 파일은 에이전트가 나르고 여기선 진행 상태만 본다 */}
                    {/* 🎯 stage 1 게이트 (2026-08-20 대표 확정: 재택 디자이너=파일 전송·재택 단계) */}
                    {/* 외주 거래처 — 진행·왕복·대금·수수료를 거래처별로 모아 보는 화면 (2026-08-10) */}
                    {/* 치과 「오늘」 — 도착 예정·작업 공정 (2026-08-11; 2026-09-29 도착완료 버튼 제거 — 예정일 경과 시 자동 소거) */}
                    <Route path="/clinic-today" element={<ClinicSubGate user={userData} title="도착 예정·작업 공정"><ClinicToday user={userData} /></ClinicSubGate>} />
                    {/* <Route path="/commission-notice" element={<CommissionNotice user={userData} />} /> 수수료 제거 */}

                    {/* 🆕 구독 / 결제 */}
                    <Route path="/upgrade" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/subscription" element={<SensitiveGate user={userData}><AppSubscription user={userData} /></SensitiveGate>} />
                    <Route path="/billing" element={<SensitiveGate user={userData}><BillingOverview user={userData} /></SensitiveGate>} />

                    {/* 기공소 설정 */}
                    <Route path="/email-settings" element={<LabEmailSettings user={userData} />} />
                    <Route path="/lab/promo-settings" element={userData?.companyId ? <Navigate to="/settings" replace /> : <LabPromoSettings user={userData} />} />
                    {/* 치과 홍보 기능 제거에 따라 관련 리다이렉트만 유지 */}
                    <Route path="/clinic/promo-register" element={<Navigate to="/" replace />} />
                    <Route path="/lab/visitor-stats" element={<LabVisitorStats user={userData} />} />

                    {/* 🆕 프로모션 랜딩페이지 (로그인 사용자) */}
                    <Route path="/promo/ivan" element={<IvanPromoLanding />} />
                    <Route path="/promo/lab" element={<LabPromoLanding />} />
                    <Route path="/promo/lab-stats" element={<LabPromoStats />} />
                    <Route path="/promo/lab-recruit" element={<LabRecruitLanding />} />
                    <Route path="/banner/lab" element={<BannerLab />} />

                    {/* 피드백 및 구매내역 */}
                    <Route path="/feedback" element={<FeedbackForm />} />
                    <Route path="/my-orders" element={<MarketplaceLayout><MyOrders userInfo={userData} /></MarketplaceLayout>} />

                    {/* 🆕 반품/환불 */}
                    <Route path="/refund-request/:orderId" element={<RefundRequest userInfo={userData} />} />
                    <Route path="/seller/refunds" element={<RefundManagement userInfo={userData} />} />

                    {/* 기타 */}
                    <Route path="/ad-manager" element={<AdManager user={userData} />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/location-terms" element={<LocationTerms onClose={() => window.history.back()} />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />

                    {/* 개인 회원 전용 - 업체 가입 */}
                    <Route
                      path="/staff-join"
                      element={
                        <StaffJoin
                          user={userData}
                          onSuccess={() => loadUserData(userData.uid)}
                        />
                      }
                    />

                    {/* 개인 회원 전용 - 받은 초대 */}
                    <Route
                      path="/staff-invitations"
                      element={<ReceivedInvites user={userData} />}
                    />

                    {/* 개인 회원 전용 - 업체 등록 */}
                    <Route
                      path="/company-register"
                      element={
                        <CompanyRegister
                          user={userData}
                        />
                      }
                    />

                    {/* 업체 오너 전용 - 직원 관리 */}
                    <Route
                      path="/company-manager"
                      element={<CompanyManager user={userData} />}
                    />
                    <Route
                      path="/staff-affiliation"
                      element={<StaffAffiliation user={userData} userData={userData} />}
                    />

                    {/* 직원 초대 코드 관리 (치과/기공소) */}
                    <Route
                      path="/staff-invite-management"
                      element={<StaffInviteManagement user={userData} />}
                    />

                    {/* 영업사원 관리 (재료회사) */}
                    <Route
                      path="/sales-rep-management"
                      element={<SalesRepManagement user={userData} />}
                    />

                    {/* 포트폴리오 피드 */}
                    <Route path="/feed" element={<PhaseRoute min={4} features={features} userData={userData} email={user?.email}><LabFeed user={userData} /></PhaseRoute>} />
                    <Route path="/portfolio/:labId" element={<PhaseRoute min={4} features={features} userData={userData} email={user?.email}><LabPortfolioGallery user={userData} /></PhaseRoute>} />

                    {/* 커뮤니티 허브 */}
                    <Route path="/community" element={<CommunityHub />} />

                    {/* 🆕 커뮤니티 기능 */}
                    <Route path="/empathy-board" element={<EmpathyBoard user={userData} />} />
                    <Route path="/community-board" element={<CommunityBoard user={userData} />} />
                    <Route path="/job-board" element={<JobBoard user={userData} />} />
                    <Route path="/new-products" element={<NewProducts user={userData} />} />
                    <Route path="/seminars" element={<Seminars user={userData} />} />
                    <Route path="/exhibition" element={<Exhibition user={userData} />} />
                    <Route path="/clinical" element={<Clinical user={userData} />} />
                    <Route path="/dental-news" element={<DentalNews user={userData} />} />
                    <Route path="/ce-education" element={<CEEducation user={userData} />} />
                    <Route path="/gov-support" element={<GovSupport user={userData} />} />
                    <Route path="/interior" element={<Interior user={userData} />} />
                    <Route path="/cleaning" element={<Cleaning user={userData} />} />

                    {/* 🆕 관리자 커뮤니티 관리 */}
                    <Route
                      path="/admin/ad-list"
                      element={<AdList user={userData} />}
                    />
                    <Route
                      path="/admin/job-management"
                      element={<JobPostManagement user={userData} />}
                    />
                    <Route
                      path="/admin/seminar-management"
                      element={<SeminarManagement user={userData} />}
                    />
                    <Route
                      path="/admin/marketplace-management"
                      element={<MarketplaceProductManagement user={userData} />}
                    />

                    {/* 관리자 승인 */}
                    <Route
                      path="/admin/ad-approval"
                      element={<AdminAdApproval userInfo={userData} />}
                    />
                    <Route
                      path="/admin/seminar-approval"
                      element={<SeminarApproval userInfo={userData} />}
                    />

                    {/* 🆕 통합 결제 시스템 관리자 페이지 */}
                    <Route
                      path="/admin/lab-approval"
                      element={<AdminLabApproval user={userData} />}
                    />
                    {/* 🏭 외주 업체 등록·서류 검토(2026-10-13 외주 의뢰서 확장) — 관리자 라우트 묶음 안 */}
                    <Route path="/admin/outsource-vendors" element={<AdminVendorReview user={userData} />} />
                    <Route
                      path="/admin/global-labs"
                      element={<AdminGlobalLabs />}
                    />
                    {/* 🌏 해외 계정 상태/홈 + 기공소찾기(영문) */}
                    <Route path="/global/status" element={<GlobalStatus user={userData} />} />
                    {/* 로그인 상태에서도 랜딩·가입 폼 접근 가능 (테스트·타계정 가입) */}
                    <Route path="/global" element={<GlobalLanding />} />
                    <Route path="/join/global" element={<JoinGlobal />} />
                    <Route path="/global/labs" element={<GlobalFindLabs user={userData} />} />
                    <Route path="/global/orders" element={<GlobalOrders user={userData} />} />
                    <Route path="/global/invoices" element={<GlobalInvoices user={userData} />} />
                    <Route path="/global/agent" element={<GlobalAgentDownload />} />
                    {/* 국내 기공소용 글로벌 수주 화면 */}
                    <Route path="/global-lab" element={<PhaseRoute min={2} features={features} userData={userData} email={user?.email}><GlobalLabOrders user={userData} /></PhaseRoute>} />
                    <Route path="/admin/global-commission" element={<AdminGlobalCommission />} />
                    <Route
                      path="/admin/lab-advertising"
                      element={<AdminLabAdvertising user={userData} />}
                    />
                    <Route
                      path="/admin/analytics"
                      element={<AdminAnalytics user={userData} />}
                    />
                    {/* AdminJobPostingApproval route removed - 결제 승인 불필요 */}
                    <Route
                      path="/admin/marketplace-approval"
                      element={<AdminMarketplaceApproval user={userData} />}
                    />

                    {/* 🆕 관리자 추가 기능 */}
                    <Route
                      path="/admin/feedbacks"
                      element={<AdminFeedbackList userInfo={userData} />}
                    />
                    <Route
                      path="/admin/payment-settings"
                      element={<AdminPaymentSettings userInfo={userData} />}
                    />
                    <Route
                      path="/admin/store-management"
                      element={<AdminStoreManagement userInfo={userData} />}
                    />
                    <Route
                      path="/admin/user-management"
                      element={<AdminUserManagement userInfo={userData} />}
                    />
                    <Route
                      path="/admin/used-item-management"
                      element={<AdminUsedItemManagement userInfo={userData} />}
                    />
                    <Route
                      path="/admin/page-toggle"
                      element={<AdminPageToggle userInfo={userData} />}
                    />
                    <Route
                      path="/admin/matching-management"
                      element={<AdminMatchingManagement userInfo={userData} />}
                    />
                    <Route path="/admin/match-requests" element={<AdminMatchRequests />} />
                    {/* 🦷 A/S 재제작 상담·연결 관리 (치과 신청 → 선택 기공소 중 1곳 배정·연결) */}
                    <Route path="/admin/as-remake" element={<AdminAsRemakeRequests />} />
                    {/* 🦷 매칭 수수료 (4.5% + VAT, 매월 10일 청구) */}
                    <Route path="/admin/commissions" element={<AdminCommissions />} />
                    {/* 🦷 테스트보철 매칭 현황 */}
                    <Route path="/admin/test-cases" element={<AdminTestCases />} />
                    {/* 📸 갤러리 스폰서 관리 */}
                    <Route path="/admin/gallery-sponsors" element={<AdminGallerySponsors />} />
                    {/* 📞 A/S 컨시어지 매칭 */}
                    <Route path="/admin/as-leads" element={<AdminAsLeads />} />
                    <Route path="/admin/new-clinics" element={<AdminNewClinics />} />
                    <Route path="/admin/clinics" element={<AdminClinicDirectory />} />   {/* 전국 치과 목록 (심평원 hospitals, 2026-09-06) */}
                    <Route path="/admin/new-labs" element={<AdminNewLabs />} />
                    <Route path="/admin/referrals" element={<AdminReferrals />} />
                    {/* === 환자 플랫폼 (관리자) === */}
                    <Route path="/admin/hira-sync" element={<AdminHiraSync />} />
                    <Route path="/admin/claim-migration" element={<AdminClaimMigration />} />
                    <Route path="/admin/hospital-claim-review" element={<AdminHospitalClaimReview />} />
                    <Route path="/admin/clinic-claim-manager" element={<AdminClinicClaimManager />} />
                    <Route path="/admin/hira-nonpayment-sync" element={<AdminHiraNonpaymentSync />} />
                    <Route path="/admin/public-data-sync" element={<AdminPublicDataSync />} />
                    <Route path="/admin/localdata-sync" element={<AdminLocaldataSync />} />
                    <Route path="/admin/care-recommendations" element={<AdminCareRecommendations />} />
                    {/* === 치과 (회원) === */}
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/patient-reservations" element={<ClinicPatientReservations />} /> */}
                    <Route path="/clinic/patient-reservations" element={legacyPatientRedirect} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/send-record" element={<ClinicSendMedicalRecord />} /> */}
                    <Route path="/clinic/send-record" element={legacyPatientRedirect} />
                    {/* 스캔 수집 숨김(2026-08-08): 라우트는 유지, 플래그 OFF면 안내 후 /dashboard */}
                    {/* 스캔 수집: 판정은 ScanIntakeRoute 하나로 통일 — 플래그 && (베타 || stage 2). 구 PhaseRoute min3 제거 (2026-08-20) */}
                    <Route path="/clinic/scans" element={<ScanIntakeRoute features={features} userData={userData} email={user?.email}><ClinicSubGate user={userData} title="최근 스캔"><ClinicRecentScans user={userData} /></ClinicSubGate></ScanIntakeRoute>} />
                    <Route path="/download-agent" element={<ClinicSubGate user={userData} title="덴클 프로그램"><AgentDownload user={userData} /></ClinicSubGate>} />
                    {/* 🧩 지그 (통합) — '지그 생성'(/jig-local) + '지그 결과'(구 /jig) 를 하나의 상태 테이블로 합침 (2026-10-21) */}
                    {/* 노출 = jigLocal 또는 jigWeb 플래그 ON(또는 베타), 기공소 한정 — 둘 중 하나라도 켜지면 통합 화면. 그 외 /dashboard. 과금 로직 없음(사용량 표시만) */}
                    {/* 로딩 중(features·userData 미로딩)엔 리다이렉트하지 않는다 — 안 그러면 지그가 잠깐 떴다 /dashboard 로 튕긴다(2026-10-22 대표 신고) */}
                    <Route path="/jig" element={(features === null || userData === null) ? null : ((isJigLocalOn(features, userData, user?.email) || isJigWebOn(features, userData, user?.email)) && (userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab')) ? <Jig user={userData} /> : <Navigate to="/dashboard" replace />} />
                    {/* 🧩 (회귀 방지·라우트 보존) 구 '지그 생성' 안내(로컬 뷰어·프로그램 다운로드)는 /jig-local 로 유지 — 플래그·게이팅 기존대로 (2026-10-19) */}
                    {/* 🏭 외주 의뢰 (PC 케이스, 2026-10-30) — 지그와 같은 게이트(에이전트 PC 목록을 쓴다). 지그 동의 화면을 같이 쓴다 */}
                    <Route path="/outsource-send" element={<Navigate to="/outsource-request" replace />} />   {/* 10-30: 외주 의뢰서 「새 의뢰」로 합침 */}
                    <Route path="/jig-local" element={(isJigLocalOn(features, userData, user?.email) && (userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab')) ? <JigLocal /> : <Navigate to="/dashboard" replace />} />
                    {/* 🧩 (회귀 방지·라우트 보존) 구 '지그 결과'(a 클라우드 보관) 딥링크는 /jig-result 로 유지 — 통합 /jig 로 흡수됨. 환자정보 보임(jigResults 본인 labId, rules read 제한) */}
                    <Route path="/jig-result" element={(isJigWebOn(features, userData, user?.email) && (userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab')) ? <JigWeb user={userData} /> : <Navigate to="/dashboard" replace />} />
                    {/* 노출 = designResults 플래그 ON(strict, 기본 OFF), 기공소 한정. 그 외 /dashboard */}
                    <Route path="/design-results" element={(isDesignResultsOn(features) && (userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab')) ? <DesignResults user={userData} /> : <Navigate to="/dashboard" replace />} />
                    {/* 🏭 CNC 파트너(제조사) — UI 구조 단계(샘플 데이터). 노출 = 관리자(미리보기) 또는 partnerHome 스위치 ON + 제조사(대표·직원). 기공소·정산·설정은 대표만 — 직원은 businessType 도 회사 유형이라 role·companyId 로 거름 (2026-10-23) */}
                    <Route path="/partner" element={(features === null || userData === null) ? null : (isAdminUser(userData) || (isPartnerHomeOn(features, userData) && ['outsource'].includes(userData?.businessType || userData?.companyBusinessType))) ? <PartnerHome /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/partner/cases" element={(features === null || userData === null) ? null : (isAdminUser(userData) || (isPartnerHomeOn(features, userData) && ['outsource'].includes(userData?.businessType || userData?.companyBusinessType))) ? <PartnerCases /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/partner/labs" element={(features === null || userData === null) ? null : (isAdminUser(userData) || (isPartnerHomeOn(features, userData) && userData?.businessType === 'outsource' && userData?.role !== 'staff' && !userData?.companyId)) ? <PartnerLabs /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/partner/settlement" element={(features === null || userData === null) ? null : (isAdminUser(userData) || (isPartnerHomeOn(features, userData) && userData?.businessType === 'outsource' && userData?.role !== 'staff' && !userData?.companyId)) ? <PartnerSettlement /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/partner/settings" element={(features === null || userData === null) ? null : (isAdminUser(userData) || (isPartnerHomeOn(features, userData) && userData?.businessType === 'outsource' && userData?.role !== 'staff' && !userData?.companyId)) ? <PartnerSettings /> : <Navigate to="/dashboard" replace />} />
                    {/* 🦷 테스트보철(A/S 무료제작권) 중개 — 사이드바 유지 */}
                    <Route path="/test-cases" element={<PhaseRoute min={4} features={features} userData={userData} email={user?.email}><TestCaseBoard user={userData} /></PhaseRoute>} />
                    <Route path="/test-cases/create" element={<PhaseRoute min={4} features={features} userData={userData} email={user?.email}><CreateTestRequest user={userData} /></PhaseRoute>} />
                    {/* 상세는 미게이트 — A/S 컨시어지 수락 링크로 진입하는 기공소(비베타 포함) 허용 */}
                    <Route path="/test-cases/:requestId" element={<TestCaseDetail user={userData} />} />
                    {/* 🦷 A/S 재제작 상담·연결 신청 (치과 주도·명단선택·관리자 중개) — 기존 /test-cases 공개게시판 대체 진입점 */}
                    {/* 치과는 phase 무관 상시 진입 + 비구독은 ClinicSubGate 구독 권고 카드 (대표 확정 2026-08-22). 치과 외(기공소 등)는 종전 phase 4 게이트 유지 */}
                    <Route path="/as-remake" element={['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) ? <ClinicSubGate user={userData} title="A/S 재제작 상담"><AsRemakeRequest user={userData} /></ClinicSubGate> : <PhaseRoute min={4} features={features} userData={userData} email={user?.email}><AsRemakeRequest user={userData} /></PhaseRoute>} />
                    {/* 🦷 매칭 수수료 내역 (기공소용) */}
                    <Route path="/commissions" element={(userData?.userType === 'staff' || userData?.companyId) ? <Navigate to="/dashboard" replace /> : <PhaseRoute min={4} features={features} userData={userData} email={user?.email}><SensitiveGate user={userData}><CommissionList user={userData} /></SensitiveGate></PhaseRoute>} />
                    {/* 🚨 긴급외주요청 (기공소간) */}
                    <Route path="/emergency-outsource" element={<PhaseRoute min={4} features={features} userData={userData} email={user?.email}><EmergencyOutsource user={userData} /></PhaseRoute>} />
                    {/* 📈 실시간 매출 조회 (기공소) */}
                    {/* 직원(기공소·외주)은 회사 매출 열람 차단 — 서류 라우트와 동일 (2026-08-05) */}
                    <Route path="/revenue" element={isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : ((userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab') ? <RevenueQuickView user={userData} /> : <Navigate to="/orders" replace />)} />
                    {/* 🔎 누락 청구 감지 (기공소 대표 전용) — features.missingClaims && 기공소. 직원은 차단 */}
                    <Route path="/missing-claims" element={(isDocBlockedStaff || !features?.missingClaims) ? <Navigate to="/dashboard" replace /> : ((userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab') ? <MissingClaims user={userData} /> : <Navigate to="/dashboard" replace />)} />
                    {/* 🖥️ CAD 디자인 외주 (국내) — 기공소만. 🎯 stage 3 게이트 (2026-08-20 대표 확정: 디자이너 찾기=매칭=Phase 3.
                        08-14 "전 기공소 개방" 확정은 08-20 상용화 단계표로 대체 — 베타 면제, 오픈=config/features.stage>=3) */}
                    {/* 연결형 전환 P0(2026-10-15 대표 승인): 메뉴 「CAD 디자인 외주」 제거 — 화면은 외주 의뢰서 › CAD 디자인 안으로 옮겼다.
                        10-30 대표: 주소도 삭제(없는 주소 → 기본 착지). 종전엔 옛 알림·에이전트 링크 때문에 남겼었다. 종전 줄(복원용):
                        <Route path="/lab-outsource" element={(lab) ? <StageRoute min={3} ...><DesignOutsource user={userData} /></StageRoute> : <Navigate to="/dashboard" replace />} /> */}
                    {/* 📝 외주 의뢰서 전용 페이지 (#38 자동 작성, 2026-08-18) — 기공소만, 치과 차단.
                        🎯 stage 3 게이트 (2026-08-20 대표 확정 D2: 외주 축 전체=Phase 3, 디자이너 찾기와 동일) */}
                    {/* 외주 의뢰서 확장(10-13): outsourceMf ON(또는 베타)이면 stage 와 무관하게 열고, CNC 제조업체(outsource)도 받은 의뢰를 본다. OFF 면 종전 그대로 */}
                    {/* 🧾 외주업체(제조사) 대표 전용 거래명세서 (10-30) — 직원(companyId 있음)·다른 업종은 대시보드로 */}
                    <Route path="/vendor-statements" element={userData === null ? null : (userData?.businessType === 'outsource' && !userData?.companyId) ? <VendorStatements user={userData} /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/outsource-request" element={
                      (features && isOutsourceMfOn(features, userData, userData?.email || user?.email) && ['lab', 'outsource'].includes(userData?.businessType || userData?.companyBusinessType))
                        ? <OutsourceRequestPage user={userData} />
                        /* 연결형 전환 P0(10-15 대표 Q6 "전체 기공소로 바로"): 기공소는 stage 게이트 없이 연다(종전 StageRoute min 3). 결제 전(lab_limited)은 MainLayout 허용 목록 가드가 그대로 막는다 */
                        : (userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab') ? <OutsourceRequestPage user={userData} /> : <Navigate to="/dashboard" replace />} />
                    {/* S5(10-27): 플래그 OFF 여도 외주 프로필 보유 계정은 열림 — VendorSettingsRoute. 종전: features && isOutsourceMfOn(...) 만 */}
                    <Route path="/settings/outsource-vendor" element={<VendorSettingsRoute features={features} userData={userData} email={user?.email}><VendorRegister user={userData} /></VendorSettingsRoute>} />
                    {/* ⚖️ 정산시스템 (치과: 받은 세금계산서 ↔ 거래명세서 대사) */}
                    <Route path="/clinic/settlement" element={isClinicAccount ? <Navigate to="/orders" replace /> : isDocBlockedStaff ? <Navigate to="/dashboard" replace /> : <SensitiveGate user={userData}><ClinicSubGate user={userData} title="정산 확인"><ClinicSettlement user={userData} /></ClinicSubGate></SensitiveGate>} />
                    <Route path="/handwritten-order" element={<HandwrittenOrder user={userData} />} />
                    <Route path="/handwritten-template" element={<LabHandwrittenTemplate user={userData} />} />
                    <Route path="/clinic/welcome" element={<ClinicWelcome />} />
                    <Route path="/clinic-profile/:hospitalId" element={<ClinicProfile />} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/patient-app-promo" element={<ClinicPatientAppPromo user={userData} />} /> */}
                    <Route path="/clinic/patient-app-promo" element={legacyPatientRedirect} />
                    {/* 미게이트 — 비회원 /find-lab이 공개인 이상 회원 차단은 역전만 만듦 (메뉴 노출만 4차) */}
                    <Route path="/clinic/find-lab" element={<PublicLabDirectory user={userData} />} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/my-patients" element={<ClinicMyPatients />} /> */}
                    <Route path="/clinic/my-patients" element={legacyPatientRedirect} />
                    {/* 🦷 환자 관리 (2026-09-10 1단계) — 치과만, clinicPatients 플래그 또는 베타 미리보기. 오너·직원 모두 열람.
                        📜 dGate = 약관 v7.0 제4조의7 14항 재확인 잠금(시행 후, 대표 미확인 시) — /patients·/patients/:pid·/reception·/clinic-recalls·/settings/feedback·roadmap·walkin-qr */}
                    <Route path="/patients" element={
                      (['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) && isClinicPatientsOn(features, userData, userData?.email || user?.email))
                        ? dGate(<ClinicPatients user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 🧾 접수 (2026-10-03) — 치과만, deskReception 플래그 strict(베타 미리보기 없음). 오너·직원 모두(직원 businessType='staff' 대비 companyBusinessType 별도 판정 — :907 패턴). OFF면 /dashboard로 */}
                    {/* features 로딩 전(null)에는 판정하지 않고 대기 — 새로고침·직접 진입 시 /dashboard 로 튕기던 문제(10-04 검토 P2). getFeatures 는 실패해도 기본값을 돌려주므로 null 이 계속되지 않는다 */}
                    <Route path="/reception" element={
                      features === null ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', color: '#64748b' }}>
                          <div style={{ ...styles.spinner, width: 40, height: 40, marginBottom: 16 }}></div>
                          <p style={{ fontSize: 14, margin: 0 }}>{'불러오는 중...'}</p>
                          <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
                        </div>
                      ) : ((['clinic', 'dental', '치과'].includes(userData?.businessType) || ['clinic', 'dental', '치과'].includes(userData?.companyBusinessType)) && isDeskReceptionOn(features))
                        ? dGate(<DeskReception user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 🔔 재내원 안내 (2026-10-01) — 치과만, clinicRecall 플래그 strict(베타 미리보기 없음) && 환자관리 게이트. 오너·직원 모두 열람 */}
                    <Route path="/clinic-recalls" element={
                      (['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) && isClinicRecallOn(features, userData, userData?.email || user?.email))
                        ? dGate(<ClinicRecalls user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 계정 삭제 안내 — 로그인 상태에서도 같은 안내 (공개 라우터와 동일 컴포넌트) */}
                    <Route path="/account/delete" element={<AccountDeleteGuide />} />
                    {/* 덴클 케어 약관·계정 삭제 안내 — 로그인 상태에서도 같은 화면 (공개 라우터와 동일 컴포넌트) */}
                    <Route path="/care/terms" element={<CareTerms />} />
                    <Route path="/care/account-delete" element={<CareAccountDelete />} />
                    {/* 🔔 환자 자동관리 › 알림 (2026-10-08, 기획서_환자자동관리_알림파트) — 치과만, clinicRecall 또는 clinicNotify 플래그 strict(베타 미리보기 없음) && 환자관리 게이트.
                        오너·직원 모두 열람(기본 설정·치과 안내 발송은 화면에서 대표만 활성). features 로딩 전(null)에는 대기(직접 진입 시 튕김 방지). /patients/:pid 보다 정적 경로가 우선 매칭 */}
                    <Route path="/patients/alerts" element={
                      features === null ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', color: '#64748b' }}>
                          <div style={{ ...styles.spinner, width: 40, height: 40, marginBottom: 16 }}></div>
                          <p style={{ fontSize: 14, margin: 0 }}>{'불러오는 중...'}</p>
                          <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
                        </div>
                      ) : (['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) && isClinicAlertsOn(features, userData, userData?.email || user?.email))
                        ? dGate(<ClinicAlerts user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 📅 환자 자동관리 › 예약 (2026-10-11, 계획_케어앱_예약대기 W1) — 치과만, careBooking 플래그 strict(베타 미리보기 없음) && 환자관리 게이트.
                        파일럿·위탁 판은 화면 안에서 서버 available 로 다시 판정(false 면 데이터 호출 0). features 로딩 전(null)에는 대기. /patients/:pid 보다 앞 */}
                    <Route path="/patients/bookings" element={
                      features === null ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', color: '#64748b' }}>
                          <div style={{ ...styles.spinner, width: 40, height: 40, marginBottom: 16 }}></div>
                          <p style={{ fontSize: 14, margin: 0 }}>{'불러오는 중...'}</p>
                          <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
                        </div>
                      ) : (['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) && isCareBookingOn(features, userData, userData?.email || user?.email))
                        ? dGate(<ClinicBookings user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 🗺️ 서버 알림 link '/clinic/patients?tab=answers'(계약_질문로드맵_웹 §6) → 실제 라우트 /patients 로(쿼리 보존) */}
                    <Route path="/clinic/patients" element={<LegacyClinicPatientsRedirect />} />
                    <Route path="/patients/:pid" element={
                      (['clinic', 'dental', '치과'].includes(userData?.businessType || userData?.companyBusinessType) && isClinicPatientsOn(features, userData, userData?.email || user?.email))
                        ? dGate(<ClinicPatientDetail user={userData} />) : <Navigate to="/dashboard" replace />
                    } />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/offers" element={<ClinicOffersManager />} /> */}
                    <Route path="/clinic/offers" element={legacyPatientRedirect} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/profile-completeness" element={<ClinicProfileCompleteness />} /> */}
                    <Route path="/clinic/profile-completeness" element={legacyPatientRedirect} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/patient-notify" element={<ClinicPatientNotify user={userData} />} /> */}
                    <Route path="/clinic/patient-notify" element={legacyPatientRedirect} />
                    {/* 구 환자앱 폐기 09-14 — <Route path="/clinic/analytics" element={<ClinicAnalytics />} /> */}
                    <Route path="/clinic/analytics" element={legacyPatientRedirect} />

                    {/* 🆕 DC마켓 관리자 (v4) */}
                    <Route
                      path="/admin/market-dashboard"
                      element={<AdminMarketDashboard userInfo={userData} />}
                    />
                    <Route
                      path="/admin/settlement-management"
                      element={<AdminSettlementManagement userInfo={userData} />}
                    />

                    {/* 🆕 관리자 구독 */}
                    <Route
                      path="/admin/subscriptions"
                      element={<AdminSubscriptionDashboard user={userData} />}
                    />

                    {/* 🆕 매칭 서비스 */}
                    <Route
                      path="/matching-subscription"
                      element={<MatchingSubscription user={userData} />}
                    />
                    {/* 광고 등록 */}
                    <Route
                      path="/ad-registration"
                      element={<AdRegistration userInfo={userData} />}
                    />

                    {/* 기공소 프로필 페이지 (visitor-stats, promo-settings 제외) */}
                    <Route
                      path="/lab/:labId"
                      element={<LabProfile user={userData} />}
                    />
                    {/* 중복 방지: visitor-stats, promo-settings는 위에서 이미 처리됨 */}
                    {/* 기공소 찾기 (치과용) - 내부 라우트 */}
                    <Route
                      path="/lab-directory"
                      element={<LabDirectory user={userData} />}
                    />
                    {/* 기공소 홍보 (기공소 대표만, 직원 접근 차단) */}
                    <Route
                      path="/lab-advertising"
                      element={userData?.companyId ? <Navigate to="/settings" replace /> : <PhaseRoute min={4} features={features} userData={userData} email={user?.email}><LabAdvertising user={userData} /></PhaseRoute>}
                    />
                    {/* 외주업체 홍보 (외주업체용) - 내부 라우트 */}
                    <Route
                      path="/outsource-advertising"
                      element={<OutsourceAdvertising user={userData} />}
                    />

                    {/* 기본 리다이렉트 - 직원은 직원신청으로, 1차 비베타 기공소는 거래명세서, 나머지는 대시보드 */}
                    <Route path="/" element={
                      (userData?.businessType === 'seller' || userData?.sellerStatus === 'approved')
                        ? <Navigate to="/seller-dashboard" />
                        : userData?.businessType === 'outsource'
                        ? <Navigate to={isPartnerHomeOn(features, userData) ? '/partner' : '/outsource-request'} />   /* 🏭 CNC 파트너 홈은 partnerHome 스위치 ON 때만 (10-23) */
                        : ((userData?.userType === 'staff' || userData?.userType === 'individual') && !userData?.companyId)
                        ? <Navigate to="/staff-invitations" />
                        /* 결제 전 기공소(제한 모드) → 받은 의뢰서 (계획_가입승인결제흐름 W3) */
                        : userData?.accessMode === 'lab_limited'
                        ? <Navigate to="/orders" />
                        : ((userData?.businessType === 'lab' || userData?.companyBusinessType === 'lab') && features && !isBetaUser(features, userData, userData?.email || user?.email))
                        ? <Navigate to="/transactions" />
                        /* 치과(대표·직원, 베타 무관)는 대시보드 착지 (2026-08-07 대표 확정: 비베타 치과 대시보드 개방) */
                        : <Navigate to="/dashboard" />
                    } />
                    <Route path="*" element={
                      (userData?.businessType === 'seller' || userData?.sellerStatus === 'approved')
                        ? <Navigate to="/seller-dashboard" />
                        : userData?.businessType === 'outsource'
                        ? <Navigate to={isPartnerHomeOn(features, userData) ? '/partner' : '/outsource-request'} />   /* 🏭 CNC 파트너 홈은 partnerHome 스위치 ON 때만 (10-23) */
                        : ((userData?.userType === 'staff' || userData?.userType === 'individual') && !userData?.companyId)
                        ? <Navigate to="/staff-invitations" />
                        : <Navigate to="/dashboard" />
                    } />
                  </Routes>
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </Router>
    </AuthProvider >
  );
}

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '24px',
  },
  loadingText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#64748b',
  },
};

export default App;